import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
  FaChevronDown,
  FaChevronRight,
  FaPlus,
  FaRegCommentDots,
  FaRegCalendarAlt,
  FaFlag,
  FaPaperclip,
  FaAt,
  FaRegSmile,
  FaPaperPlane,
  FaBold,
  FaItalic,
  FaUnderline,
  FaListUl,
  FaQuoteRight,
  FaTimes,
  FaEllipsisH,
  FaVideo,
  FaMicrophone,
  FaCheck,
  FaUndo,
  FaUser,
  FaEdit,
  FaTrash,
  FaArrowUp,
  FaArrowDown,
  FaEye,
  FaWhatsapp,
  FaGlobe,
  FaDesktop,
  FaMicrosoft,
  FaEnvelope,
  FaShare,
} from "react-icons/fa";
import {
  GetRequest,
  PostRequest,
  PutRequest,
} from "../../Utils/hooks/ApiHandler";
import {
  formatDate,
  formatDateInAlphabeticalMonth,
  getUpdatedFields,
} from "../../Utils/helpers/commonhelper";

const STATUS_OPTIONS = [
  { value: "complete", label: "Complete", color: "#1bbf6b" },
  { value: "progress", label: "In Progress", color: "#f1c232" },
  { value: "pending", label: "Pending", color: "#87909e" },
  { value: "onhold", label: "On Hold", color: "#f2994a" },
  { value: "cancel", label: "Cancel", color: "#e5484d" },
];

const STATUS_ACTION_OPTIONS = [
  { value: "complete", label: "Complete", color: "#1bbf6b" },
  { value: "progress", label: "In Progress", color: "#f1c232" },
  { value: "pending", label: "Pending", color: "#87909e" },
  { value: "onhold", label: "On Hold", color: "#f2994a" },
  { value: "cancel", label: "Cancel", color: "#e5484d" },
];

// Copy shown in the status-change confirm modal per selected status.
const STATUS_MODAL_COPY = {
  complete: {
    title: "Complete Task",
    message: "Are you sure you want to mark as complete this task?",
    needsReason: false,
  },
  pending: {
    title: "Pending Task",
    message: "Are you sure you want to mark this task as in pending?",
  },
  progress: {
    title: "Progress Task",
    message: "Are you sure you want to mark this task as in progress?",
  },
  onhold: {
    title: "On Hold Task",
    message: "Are you sure you want to mark this task as in On Hold?",
  },
  cancel: {
    title: "Close Task",
    message: "Are you sure you want to cancel this task?",
    needsReason: true,
    reasonPlaceholder: "Please provide a reason for cancellation",
  },
};

const STAGE_BADGE_COLORS = [
  "#1a8cff",
  "#7B68EE",
  "#1bbf6b",
  "#e5484d",
  "#f1c232",
  "#00b8d9",
];

const OPEN_STAGE_KEYWORDS = ["to do", "todo", "open", "backlog", "new"];

const isOpenStage = (stageName = "") =>
  OPEN_STAGE_KEYWORDS.some((kw) => stageName.toLowerCase().includes(kw));

const getStageBadgeColor = (stage, index) =>
  stage?.color || STAGE_BADGE_COLORS[index % STAGE_BADGE_COLORS.length];

const PRIORITY_OPTIONS = [
  { value: "urgent", label: "Urgent", color: "#f2394a" },
  { value: "high", label: "High", color: "#f2a93b" },
  { value: "normal", label: "Normal", color: "#4592f0" },
  { value: "low", label: "Low", color: "#8590a2" },
];

const EMOJI_QUICK_LIST = [
  "😀",
  "😂",
  "😍",
  "👍",
  "🙌",
  "🎉",
  "🔥",
  "✅",
  "❤️",
  "😢",
  "🤔",
  "🚀",
];

const toISO = (d) => {
  if (!d) return null;
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return null;
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(
    dt.getDate(),
  ).padStart(2, "0")}`;
};

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

// Default start date = today, default end date = one day after start date.
const todayISO = () => toISO(new Date());
const tomorrowISO = () => toISO(addDays(new Date(), 1));

// Zod preprocessor: accepts Date or string, requires a non-empty date string.
const asDateString = (msg) =>
  z.preprocess(
    (val) => {
      if (val instanceof Date) return val.toISOString().split("T")[0]; // YYYY-MM-DD format
      return val;
    },
    z.string().min(1, msg),
  );

const isSameDay = (a, b) =>
  a && b && new Date(a).toDateString() === new Date(b).toDateString();

const isToday = (d) => d && isSameDay(d, new Date());

const isYesterday = (d) => d && isSameDay(d, addDays(new Date(), -1));

const isOverdue = (d) =>
  d && startOfDay(new Date(d)).getTime() < startOfDay(new Date()).getTime();
// Builds a WhatsApp-ready number: prefixes the country code (default "91")
// unless the phone number already appears to include it.
const toWhatsappNumber = (phone, countryCode) => {
  const cleanPhone = String(phone || "").replace(/\D/g, "");
  if (!cleanPhone) return "";
  const cc = String(countryCode || "91").replace(/\D/g, "") || "91";
  if (cleanPhone.startsWith(cc)) return cleanPhone;
  return `${cc}${cleanPhone}`;
};

const parseOwnerId = (raw) => {
  let value = raw;
  let attempts = 0;
  while (typeof value === "string" && value.trim() && attempts < 5) {
    try {
      value = JSON.parse(value);
    } catch (e) {
      break;
    }
    attempts += 1;
  }
  if (Array.isArray(value)) {
    return value.filter(Boolean).map((o) => ({
      value: o.value ?? o.id,
      label: o.label ?? o.name ?? "",
      number: o.number ?? o.mobile_number ?? o.mobile ?? o.phone ?? "",
    }));
  }
  return [];
};

const normalizeTask = (task) => ({
  ...task,
  assignees: parseOwnerId(task?.owner_id),
  due_date: task?.due_date || task?.end_date || null,
});

const nextWeekday = (from, targetDow) => {
  const x = startOfDay(from);
  const diff = (targetDow + 7 - x.getDay()) % 7 || 7;
  return addDays(x, diff);
};

const buildMonthGrid = (year, month) => {
  const first = new Date(year, month, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  return cells;
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const AVATAR_COLORS = [
  "#7B68EE",
  "#4592f0",
  "#1bbf6b",
  "#f1c232",
  "#f2a93b",
  "#e5484d",
  "#00b8d9",
  "#d6409f",
  "#36b37e",
  "#8590a2",
];

const getAvatarColor = (seed = "U") => {
  const str = String(seed || "U");
  let hash = 0;
  for (let i = 0; i < str.length; i++)
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase() || "U";

const Avatar = ({ name, image, id, size = 24 }) =>
  image ? (
    <img
      src={image}
      alt={name || "User"}
      title={name || undefined}
      className="tl-avatar-img"
      style={{ width: size, height: size }}
    />
  ) : (
    <div
      className="tl-avatar"
      title={name || undefined}
      style={{
        width: size,
        height: size,
        background: getAvatarColor(id || name),
        fontSize: size <= 22 ? 9 : 10,
      }}
    >
      {getInitials(name)}
    </div>
  );

// Global popover close handler
let closeAllPopovers = null;

const Popover = ({ onClose, children, style, className = "", anchorRect }) => {
  const popoverRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!anchorRect || !popoverRef.current) return;
    const popoverEl = popoverRef.current;
    const popoverWidth = popoverEl.offsetWidth;
    const popoverHeight = popoverEl.offsetHeight;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = anchorRect.bottom + 6;
    let left = anchorRect.left;

    if (top + popoverHeight > viewportHeight - 10) {
      top = anchorRect.top - popoverHeight - 6;
    }
    if (top < 10) top = 10;

    if (left + popoverWidth > viewportWidth - 10) {
      left = viewportWidth - popoverWidth - 10;
    }
    if (left < 10) left = 10;

    setPosition({ top, left });
  }, [anchorRect]);

  useEffect(() => {
    closeAllPopovers = onClose;

    const handleOutsideMouseDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", handleOutsideMouseDown, true);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideMouseDown, true);
      document.removeEventListener("keydown", handleEscape);
      if (closeAllPopovers === onClose) {
        closeAllPopovers = null;
      }
    };
  }, [onClose]);

  return (
    <>
      <div className="tl-popover-backdrop" />
      <div
        ref={popoverRef}
        className={`tl-popover ${className}`}
        style={{
          ...style,
          top: position.top,
          left: position.left,
          position: "fixed",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </>
  );
};

const AssigneePopover = ({
  options,
  selected,
  onChange,
  onClose,
  anchorRect,
}) => {
  const [query, setQuery] = useState("");
  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(query.toLowerCase()),
  );
  const isSelected = (opt) => selected.some((s) => s.value === opt.value);
  const toggle = (opt) => {
    if (isSelected(opt))
      onChange(selected.filter((s) => s.value !== opt.value));
    else onChange([...selected, opt]);
  };

  return (
    <Popover
      onClose={onClose}
      className="tl-assignee-popover"
      anchorRect={anchorRect}
    >
      <input
        autoFocus
        className="tl-popover-search"
        placeholder="Search or enter email..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="tl-popover-section-label">People</div>
      <div className="tl-popover-list">
        {filtered.length === 0 && (
          <div className="tl-popover-empty">No matches</div>
        )}
        {filtered.map((opt) => (
          <div
            key={opt.value}
            className={`tl-popover-row ${isSelected(opt) ? "tl-popover-row-active" : ""}`}
            onClick={() => toggle(opt)}
          >
            <Avatar name={opt.label} id={opt.value} size={22} />
            <span className="fs-13 flex-grow-1">{opt.label}</span>
            {isSelected(opt) && <span className="tl-check">✓</span>}
          </div>
        ))}
      </div>
    </Popover>
  );
};

const ShareAssigneePopover = ({
  assignees = [],
  onSelect,
  onClose,
  anchorRect,
}) => (
  <Popover
    onClose={onClose}
    className="tl-assignee-popover"
    anchorRect={anchorRect}
  >
    <div className="tl-popover-section-label">Send WhatsApp to</div>
    <div className="tl-popover-list">
      {assignees.length === 0 && (
        <div className="tl-popover-empty">No assignees</div>
      )}
      {assignees.map((opt) => (
        <div
          key={opt.value}
          className="tl-popover-row"
          onClick={() => {
            onSelect(opt);
            onClose();
          }}
        >
          <Avatar name={opt.label} id={opt.value} size={22} />
          <span className="fs-13 flex-grow-1">{opt.label}</span>
          <FaWhatsapp size={12} style={{ color: "#25D366" }} />
        </div>
      ))}
    </div>
  </Popover>
);

const WhatsappPlatformPopover = ({
  assignee,
  onSelect,
  onClose,
  anchorRect,
}) => (
  <Popover
    onClose={onClose}
    className="tl-assignee-popover"
    anchorRect={anchorRect}
  >
    <div className="tl-popover-section-label">
      Send to {assignee?.label || "assignee"} on
    </div>
    <div className="tl-popover-list">
      <div
        className="tl-popover-row"
        onClick={() => {
          onSelect("web");
          onClose();
        }}
      >
        <FaGlobe size={12} style={{ color: "#25D366" }} />
        <span className="fs-13 flex-grow-1 ms-2">WhatsApp Web</span>
      </div>
      <div
        className="tl-popover-row"
        onClick={() => {
          onSelect("desktop");
          onClose();
        }}
      >
        <FaDesktop size={12} style={{ color: "#25D366" }} />
        <span className="fs-13 flex-grow-1 ms-2">WhatsApp Desktop</span>
      </div>
      <div
        className="tl-popover-row"
        onClick={() => {
          onSelect("teams");
          onClose();
        }}
      >
        <FaMicrosoft size={12} style={{ color: "#5059C9" }} />
        <span className="fs-13 flex-grow-1 ms-2">Teams(Business)</span>
      </div>
      <div
        className="tl-popover-row"
        onClick={() => {
          onSelect("outlook");
          onClose();
        }}
      >
        <FaEnvelope size={12} style={{ color: "#0078D4" }} />
        <span className="fs-13 flex-grow-1 ms-2">Outlook</span>
      </div>
      {/* <div
        className="tl-popover-row"
        onClick={() => {
          onSelect("email");
          onClose();
        }}
      >
        <FaEnvelope size={12} style={{ color: "#87909e" }} />
        <span className="fs-13 flex-grow-1 ms-2">Email</span>
      </div> */}
      <div
        className="tl-popover-row"
        onClick={() => {
          onSelect("teams-personal");
          onClose();
        }}
      >
        <FaMicrosoft size={12} style={{ color: "#5059C9" }} />
        <span className="fs-13 flex-grow-1 ms-2">Teams (Personal)</span>
      </div>
    </div>
  </Popover>
);

const DatePopover = ({
  startDate,
  dueDate,
  activeField,
  onChange,
  onClose,
  anchorRect,
}) => {
  const [field, setField] = useState(activeField || "due_date");
  const seed = (field === "start_date" ? startDate : dueDate) || new Date();
  const [viewYear, setViewYear] = useState(new Date(seed).getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date(seed).getMonth());

  const [localSelection, setLocalSelection] = useState({
    start_date: null,
    due_date: null,
  });

  const applyDate = (d) => {
    setLocalSelection((prev) => ({ ...prev, [field]: d }));
    onChange({ [field]: toISO(d) });
  };

  const effectiveStartDate = localSelection.start_date || startDate;
  const isBeforeStartDate = (d) =>
    field === "due_date" &&
    effectiveStartDate &&
    startOfDay(d).getTime() <
    startOfDay(new Date(effectiveStartDate)).getTime();

  const quickOptions = [
    {
      label: "Today",
      sub: MONTH_NAMES[new Date().getMonth()].slice(0, 3),
      get: () => new Date(),
    },
    { label: "Tomorrow", sub: "", get: () => addDays(new Date(), 1) },
    { label: "This weekend", sub: "", get: () => nextWeekday(new Date(), 6) },
    { label: "Next week", sub: "", get: () => nextWeekday(new Date(), 1) },
    {
      label: "Next weekend",
      sub: "",
      get: () => addDays(nextWeekday(new Date(), 6), 7),
    },
    { label: "2 weeks", sub: "", get: () => addDays(new Date(), 14) },
    { label: "4 weeks", sub: "", get: () => addDays(new Date(), 28) },
  ];

  const grid = buildMonthGrid(viewYear, viewMonth);
  const selectedForField =
    localSelection[field] || (field === "start_date" ? startDate : dueDate);

  const changeMonth = (delta) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) {
      m = 11;
      y -= 1;
    }
    if (m > 11) {
      m = 0;
      y += 1;
    }
    setViewMonth(m);
    setViewYear(y);
  };

  return (
    <Popover
      onClose={onClose}
      className="tl-date-popover"
      anchorRect={anchorRect}
    >
      <div className="tl-date-tabs">
        <button
          type="button"
          className={`tl-date-tab ${field === "start_date" ? "tl-date-tab-active" : ""}`}
          onClick={() => setField("start_date")}
        >
          {localSelection.start_date || startDate
            ? formatDateInAlphabeticalMonth(
              localSelection.start_date || startDate,
            )
            : "Start date"}
        </button>
        <span className="tl-muted">→</span>
        <button
          type="button"
          className={`tl-date-tab ${field === "due_date" ? "tl-date-tab-active" : ""}`}
          onClick={() => setField("due_date")}
        >
          {localSelection.due_date || dueDate
            ? formatDateInAlphabeticalMonth(localSelection.due_date || dueDate)
            : "Due date"}
        </button>
        <span className="tl-date-popover-close" title="Close" onClick={onClose}>
          <FaTimes size={12} />
        </span>
      </div>

      <div className="tl-date-body">
        <div className="tl-date-quick">
          {quickOptions.map((q) => {
            const disabled = isBeforeStartDate(q.get());
            return (
              <div
                key={q.label}
                className={`tl-date-quick-row${disabled ? " tl-date-quick-row-disabled" : ""}`}
                onClick={() => !disabled && applyDate(q.get())}
              >
                <span>{q.label}</span>
                <span className="tl-muted fs-11">
                  {toISO(q.get()) === toISO(new Date())
                    ? "Today"
                    : q
                      .get()
                      .toLocaleDateString(undefined, { weekday: "short" })}
                </span>
              </div>
            );
          })}
          <div className="tl-date-quick-row tl-date-recurring">
            <span>Set Recurring</span>
            <FaChevronRight size={10} />
          </div>
        </div>

        <div className="tl-date-calendar">
          <div className="tl-date-cal-header">
            <span className="fw-semibold fs-13">
              {MONTH_NAMES[viewMonth]} {viewYear}
            </span>
            <div className="d-flex gap-2">
              <span className="tl-cal-nav" onClick={() => changeMonth(-1)}>
                ‹
              </span>
              <span className="tl-cal-nav" onClick={() => changeMonth(1)}>
                ›
              </span>
            </div>
          </div>
          <div className="tl-date-cal-grid tl-date-cal-dow">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
          <div className="tl-date-cal-grid">
            {grid.map((d, i) => {
              const disabled = d && isBeforeStartDate(d);
              return (
                <span
                  key={i}
                  className={[
                    "tl-cal-cell",
                    !d && "tl-cal-cell-empty",
                    d && isToday(d) && "tl-cal-cell-today",
                    d &&
                    selectedForField &&
                    isSameDay(d, selectedForField) &&
                    "tl-cal-cell-selected",
                    disabled && "tl-cal-cell-disabled",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => d && !disabled && applyDate(d)}
                >
                  {d ? d.getDate() : ""}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </Popover>
  );
};

const OptionPopover = ({
  options,
  onSelect,
  onClose,
  allowClear,
  anchorRect,
}) => (
  <Popover
    onClose={onClose}
    className="tl-option-popover"
    anchorRect={anchorRect}
  >
    {options.map((opt) => (
      <div
        key={opt.value}
        className="tl-popover-row"
        onClick={() => onSelect(opt)}
      >
        <span className="tl-dot" style={{ background: opt.color }} />
        <span className="fs-13">{opt.label}</span>
      </div>
    ))}
    {allowClear && (
      <div className="tl-popover-row tl-muted" onClick={() => onSelect(null)}>
        <span className="fs-13">Clear</span>
      </div>
    )}
  </Popover>
);

const MoreOptionsPopover = ({
  onEdit,
  onDelete,
  onClose,
  anchorRect,
  taskGrant,
  onShare,
  onWhatsappShare
}) => (
  <Popover onClose={onClose} className="" anchorRect={anchorRect}>
    <div
      className="tl-popover-row"
      onClick={() => {
        if (!taskGrant?.isEdit)
          return toast.warning("You are not authorized for this action!");

        onEdit();
        onClose();
      }}
    >
      <FaEdit size={12} className="me-1" />
      <span className="fs-13">Edit</span>
    </div>
    <div
      className="tl-popover-row tl-danger"
      onClick={() => {
        onWhatsappShare();
        onClose();
      }}
    >
      <FaShare size={12} className="me-1" />
      <span className="fs-13">Share</span>
    </div>
    {/* <div
      className="tl-popover-row tl-danger"
      onClick={() => {
        onShare();
        onClose();
      }}
    >
      <FaWhatsapp size={12} className="me-1" />
      <span className="fs-13">Share</span>
    </div> */}
    {/* <div
      className="tl-popover-row tl-danger"
      onClick={() => {
        onDelete();
        onClose();
      }}
    >
      <FaTrash size={12} className="me-1" />
      <span className="fs-13">Delete</span>
    </div> */}
  </Popover>
);

const EditTaskPopover = ({
  task_id,
  assigneeOpts,
  onClose,
  onSave,
  customerId,
  taskGrant,
  anchorRect,
  user_login_id,
  headers,
  extras,
}) => {
  const [openField, setOpenField] = useState(null);
  const [subAnchorRect, setSubAnchorRect] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [detailLoading, setDetailLoading] = useState(true);
  const inputRef = useRef(null);
  const [task, setTask] = useState([]);
  const { control, handleSubmit, getValues, setValue, reset } = useForm({
    resolver: zodResolver(addTaskSchema),
    defaultValues: {
      task_name: task?.task_name || "",
      assignees: task?.assignees || [],
      priority:
        PRIORITY_OPTIONS.find((p) => p.value === task?.priority) || null,
      start_date: task?.start_date || todayISO(),
      due_date: task?.due_date || tomorrowISO(),
    },
  });

  useEffect(() => {
    if (!detailLoading) inputRef.current?.focus();
  }, [detailLoading]);

  const fetchDetailsOfTaskForEdit = async () => {
    if (!task_id) return;
    setDetailLoading(true);
    try {
      const res = await GetRequest(
        `project/getDetailsOfTaskForEdit/${task_id}/${customerId}/${user_login_id}`,
        { headers },
        extras,
      );
      const taskData = res?.data;
      if (taskData) {
        setTask(taskData);
        let parsedAssignees = [];
        if (taskData?.owner_id) {
          try {
            parsedAssignees =
              typeof taskData.owner_id === "string"
                ? JSON.parse(taskData.owner_id)
                : taskData.owner_id;
          } catch (e) {
            console.error("Failed to parse owner_id:", e);
          }
        }
        const matchedPriority =
          PRIORITY_OPTIONS.find(
            (p) => p.value?.toLowerCase() === taskData?.priority?.toLowerCase(),
          ) || null;
        const resolvedStartDate = taskData?.start_date || todayISO();
        reset({
          task_name: taskData?.task_name || "",
          assignees: parsedAssignees,
          priority: matchedPriority,
          start_date: resolvedStartDate,
          due_date: taskData?.end_date || toISO(addDays(resolvedStartDate, 1)),
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchDetailsOfTaskForEdit();
  }, [task_id]);
  const openSubPopover = (field) => (e) => {
    e.stopPropagation();
    setSubAnchorRect(e.currentTarget.getBoundingClientRect());
    setOpenField(field);
  };
  const closeSubPopover = () => {
    setOpenField(null);
    setSubAnchorRect(null);
  };

  const onFormError = (errors) => {
    if (errors?.due_date) {
      toast.error(errors.due_date.message || "End date is required");
    } else if (errors?.start_date) {
      toast.error(errors.start_date.message || "Start date is required");
    }
  };

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        task_id: task?.id,
        project_id: task?.project_id,
        stage_id: task?.project_stage_id,
        task_name: values.task_name,
        task_status: task?.task_status || "open",
        priority: values.priority?.value || null,
        owner_id: parseOwnerId(values.assignees),
        start_date: values.start_date || todayISO(),
        end_date: values.due_date || tomorrowISO(),
        tags: task?.tags,
        description: task?.description || "",
      };
      await onSave(task, payload);
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Could not update task");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popover
      onClose={onClose}
      className="tl-edit-popover"
      anchorRect={anchorRect}
    >
      {detailLoading ? (
        <div className="tl-edit-popover-loading">
          <span className="tl-loading-spinner" />
        </div>
      ) : (
        <>
          <Controller
            name="task_name"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                ref={inputRef}
                className="tl-edit-popover-input"
                placeholder="Task Name"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) =>
                  e.key === "Enter" && handleSubmit(onSubmit, onFormError)()
                }
              />
            )}
          />

          <div className="tl-edit-popover-icons">
            <Controller
              name="assignees"
              control={control}
              render={({ field }) => (
                <span
                  className="tl-add-icon"
                  style={{ position: "relative" }}
                  onClick={openSubPopover("assignee")}
                >
                  <FaAt
                    size={12}
                    style={{
                      color:
                        field.value && field.value.length > 0
                          ? "#2563eb"
                          : "#6b7280",
                    }}
                  />
                  {openField === "assignee" && (
                    <AssigneePopover
                      options={assigneeOpts}
                      selected={field.value || []}
                      onChange={(sel) => field.onChange(sel)}
                      onClose={closeSubPopover}
                      anchorRect={subAnchorRect}
                    />
                  )}
                </span>
              )}
            />

            <Controller
              name="due_date"
              control={control}
              render={({ field }) => (
                <span
                  className="tl-add-icon"
                  style={{ position: "relative" }}
                  onClick={openSubPopover("date")}
                >
                  <FaRegCalendarAlt
                    size={12}
                    style={{
                      color: field.value ? "#2563eb" : "#6b7280",
                    }}
                  />
                  {openField === "date" && (
                    <DatePopover
                      startDate={getValues("start_date")}
                      dueDate={field.value}
                      activeField="due_date"
                      onChange={(patch) => {
                        if (patch.due_date !== undefined)
                          field.onChange(patch.due_date);
                        if (patch.start_date !== undefined) {
                          setValue("start_date", patch.start_date);
                          // Keep end date one day after the newly picked start date.
                          field.onChange(toISO(addDays(patch.start_date, 1)));
                        }
                      }}
                      onClose={closeSubPopover}
                      anchorRect={subAnchorRect}
                    />
                  )}
                </span>
              )}
            />

            <Controller
              name="priority"
              control={control}
              render={({ field }) => (
                <span
                  className="tl-add-icon"
                  style={{ position: "relative" }}
                  onClick={openSubPopover("priority")}
                >
                  <FaFlag
                    size={12}
                    style={{
                      color:
                        field.value?.color ||
                        (field.value ? "#ef4444" : "#6b7280"),
                    }}
                  />
                  {openField === "priority" && (
                    <OptionPopover
                      options={PRIORITY_OPTIONS}
                      allowClear
                      onSelect={(opt) => {
                        field.onChange(opt);
                        closeSubPopover();
                      }}
                      onClose={closeSubPopover}
                      anchorRect={subAnchorRect}
                    />
                  )}
                </span>
              )}
            />
          </div>

          <div className="tl-edit-popover-actions">
            <button
              type="button"
              className="tl-btn-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="tl-btn-save"
              onClick={handleSubmit(onSubmit, onFormError)}
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Save"}
            </button>
          </div>
        </>
      )}
    </Popover>
  );
};

const ChangeStatusModal = ({
  status,
  submitting,
  onCancel,
  onSubmit,
  taskGrant,
}) => {
  const copy = STATUS_MODAL_COPY[status?.value] || STATUS_MODAL_COPY.complete;
  console.log(status?.value, "value");
  const [reason, setReason] = useState("");

  return (
    <div className="tl-status-modal-backdrop" onMouseDown={onCancel}>
      <div className="tl-status-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="tl-status-modal-header">
          <span>{copy.title}</span>
          <span className="tl-status-modal-close" onClick={onCancel}>
            <FaTimes size={14} />
          </span>
        </div>
        <div className="tl-status-modal-body">
          <p className="tl-status-modal-message">{copy.message}</p>
          {copy.needsReason && (
            <>
              <label className="tl-status-modal-label">Reason</label>
              <textarea
                autoFocus
                className="tl-status-modal-textarea"
                placeholder={copy.reasonPlaceholder}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </>
          )}
        </div>
        <div className="tl-status-modal-footer">
          <button
            className="tl-btn-cancel"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="tl-status-modal-submit"
            onClick={() => onSubmit(copy.needsReason ? reason : undefined)}
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

const CustomCommentEditor = ({ onSubmit, assigneeOptions = [], taskGrant }) => {
  const editorRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [showFormatBar, setShowFormatBar] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showMention, setShowMention] = useState(false);
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [emojiAnchorRect, setEmojiAnchorRect] = useState(null);
  const [mentionAnchorRect, setMentionAnchorRect] = useState(null);

  const focusEditor = () => editorRef.current?.focus();

  const insertAtCursor = (text) => {
    focusEditor();
    document.execCommand("insertText", false, text);
    setIsEmpty(!editorRef.current?.textContent?.trim());
  };

  const runFormat = (cmd) => {
    focusEditor();
    document.execCommand(cmd, false, null);
  };

  const handleInput = () => {
    setIsEmpty(!editorRef.current?.textContent?.trim());
  };

  const handleSend = () => {
    const html = editorRef.current?.innerHTML || "";
    const text = editorRef.current?.textContent || "";
    if (!text.trim()) return;
    onSubmit(html, files);
    editorRef.current.innerHTML = "";
    setIsEmpty(true);
    setFiles([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className="tl-comment-editor"
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        padding: "8px 10px",
        backgroundColor: "#ffffff",
      }}
    >
      {files.length > 0 && (
        <div
          className="tl-comment-files"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "4px",
            marginBottom: "6px",
          }}
        >
          {files.map((f, i) => (
            <span
              key={i}
              className="tl-comment-file-chip"
              style={{
                fontSize: "11px",
                background: "#f3f4f6",
                padding: "2px 6px",
                borderRadius: "4px",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <FaPaperclip size={10} className="me-1" />
              {f.name}
              <FaTimes
                size={9}
                className="ms-1"
                style={{ cursor: "pointer" }}
                onClick={() =>
                  setFiles((prev) => prev.filter((_, idx) => idx !== i))
                }
              />
            </span>
          ))}
        </div>
      )}

      {showFormatBar && (
        <div
          className="tl-comment-format-bar"
          style={{
            display: "flex",
            gap: "8px",
            paddingBottom: "6px",
            borderBottom: "1px solid #f3f4f6",
            marginBottom: "6px",
            cursor: "pointer",
            color: "#6b7280",
          }}
        >
          <span onClick={() => runFormat("bold")}>
            <FaBold size={11} />
          </span>
          <span onClick={() => runFormat("italic")}>
            <FaItalic size={11} />
          </span>
          <span onClick={() => runFormat("underline")}>
            <FaUnderline size={11} />
          </span>
          <span onClick={() => runFormat("insertUnorderedList")}>
            <FaListUl size={11} />
          </span>
          <span onClick={() => runFormat("formatBlock", "blockquote")}>
            <FaQuoteRight size={11} />
          </span>
        </div>
      )}

      <div
        ref={editorRef}
        className="tl-comment-input"
        contentEditable
        data-placeholder="Write a comment..."
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        style={{
          minHeight: "36px",
          maxHeight: "80px",
          overflowY: "auto",
          outline: "none",
          fontSize: "13px",
          color: "#1f2937",
        }}
      />

      <div
        className="tl-comment-toolbar"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginTop: "6px",
          paddingTop: "4px",
        }}
      >
        <span
          className="tl-comment-tool"
          title="More"
          style={{ cursor: "pointer", color: "#6b7280" }}
          onClick={() => toast.info("More options coming soon")}
        >
          <FaPlus size={11} />
        </span>
        <span
          className="tl-comment-tool tl-comment-tool-label"
          style={{
            cursor: "pointer",
            color: "#6b7280",
            fontSize: "12px",
            display: "inline-flex",
            alignItems: "center",
            gap: "2px",
          }}
          onClick={() => setShowFormatBar((v) => !v)}
        >
          Comment <FaChevronDown size={8} />
        </span>
        <input
          type="file"
          multiple
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={(e) => {
            setFiles((prev) => [...prev, ...Array.from(e.target.files || [])]);
            e.target.value = "";
          }}
        />
        <span
          className="tl-comment-tool"
          title="Attach"
          style={{ cursor: "pointer", color: "#6b7280" }}
          onClick={() => fileInputRef.current?.click()}
        >
          <FaPaperclip size={11} />
        </span>
        <span
          className="tl-comment-tool"
          title="Mention"
          style={{ position: "relative", cursor: "pointer", color: "#6b7280" }}
          onClick={(e) => {
            setMentionAnchorRect(e.currentTarget.getBoundingClientRect());
            setShowMention((v) => !v);
          }}
        >
          <FaAt size={11} />
          {showMention && (
            <AssigneePopover
              options={assigneeOptions}
              selected={[]}
              onChange={(sel) => {
                if (sel[0]) insertAtCursor(`@${sel[0].label} `);
                setShowMention(false);
                setMentionAnchorRect(null);
              }}
              onClose={() => {
                setShowMention(false);
                setMentionAnchorRect(null);
              }}
              anchorRect={mentionAnchorRect}
            />
          )}
        </span>
        <span
          className="tl-comment-tool"
          title="Emoji"
          style={{ position: "relative", cursor: "pointer", color: "#6b7280" }}
          onClick={(e) => {
            setEmojiAnchorRect(e.currentTarget.getBoundingClientRect());
            setShowEmoji((v) => !v);
          }}
        >
          <FaRegSmile size={11} />
          {showEmoji && (
            <Popover
              onClose={() => {
                setShowEmoji(false);
                setEmojiAnchorRect(null);
              }}
              className="tl-emoji-popover"
              anchorRect={emojiAnchorRect}
            >
              {EMOJI_QUICK_LIST.map((e) => (
                <span
                  key={e}
                  className="tl-emoji-item"
                  onClick={() => {
                    insertAtCursor(e);
                    setShowEmoji(false);
                    setEmojiAnchorRect(null);
                  }}
                >
                  {e}
                </span>
              ))}
            </Popover>
          )}
        </span>
        <span
          className="tl-comment-tool"
          title="GIF"
          style={{ cursor: "pointer", color: "#6b7280" }}
          onClick={() => toast.info("GIF picker not wired up yet")}
        >
          <FaVideo size={11} />
        </span>
        <span
          className="tl-comment-tool"
          title="Voice note"
          style={{ cursor: "pointer", color: "#6b7280" }}
          onClick={() => toast.info("Voice notes not wired up yet")}
        >
          <FaMicrophone size={11} />
        </span>

        {/* Send Button */}
        <button
          type="button"
          className="tl-comment-send"
          disabled={isEmpty}
          onClick={handleSend}
          style={{
            marginLeft: "auto",
            border: "none",
            backgroundColor: isEmpty ? "#e5e7eb" : "#2563eb",
            color: isEmpty ? "#9ca3af" : "#ffffff",
            padding: "5px 8px",
            borderRadius: "4px",
            cursor: isEmpty ? "not-allowed" : "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FaPaperPlane size={10} />
        </button>
      </div>
    </div>
  );
};

const CommentThreadPopover = ({
  task,
  comments,
  loading,
  currentUser,
  assigneeOptions,
  taskGrant,
  onAddComment,
  onClose,
  anchorRect,
}) => (
  <Popover
    onClose={onClose}
    className="tl-comment-popover"
    anchorRect={anchorRect}
    style={{
      maxWidth: "380px",
      width: "360px",
      maxHeight: "480px",
      height: "440px",
      display: "flex",
      flexDirection: "column",
      borderRadius: "10px",
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
      backgroundColor: "#ffffff",
      overflow: "hidden",
      padding: "0",
    }}
  >
    {/* Comments List Container with Active Scrollbar */}
    <div
      className="tl-comment-thread-list"
      style={{
        flex: "1 1 auto",
        overflowY: "auto",
        padding: "12px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        minHeight: "0",
      }}
    >
      {loading && <div className="tl-muted fs-12 p-2">Loading…</div>}
      {!loading && comments.length === 0 && (
        <div className="tl-muted fs-12 p-2 text-center">No comments yet.</div>
      )}
      {comments.map((c, i) => (
        <div
          className="tl-comment-item-box"
          key={c.id || i}
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "8px 12px",
            backgroundColor: "#f9fafb",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          {/* Header */}
          <div className="d-flex align-items-center gap-2">
            <Avatar
              name={c.user_name || c.creator_name}
              image={c.user_image}
              id={c.created_by || c.user_name}
              size={22}
            />
            <span className="fw-semibold fs-13" style={{ color: "#1f2937" }}>
              {c.user_name || "User"}
            </span>
            <span
              className="tl-muted fs-11"
              style={{ marginLeft: "auto", color: "#9ca3af" }}
            >
              {c.created_at
                ? formatDateInAlphabeticalMonth(c.created_at)
                : "Just now"}
            </span>
          </div>

          {/* Comment Body */}
          <div
            className="fs-13 tl-comment-text"
            style={{ color: "#374151", wordBreak: "break-word" }}
            dangerouslySetInnerHTML={{ __html: c.description }}
          />
        </div>
      ))}
    </div>

    {/* Bottom Fixed Comment Editor Box */}
    <div
      style={{
        flexShrink: 0,
        padding: "10px",
        backgroundColor: "#ffffff",
        borderTop: "1px solid #f3f4f6",
      }}
    >
      <CustomCommentEditor
        assigneeOptions={assigneeOptions}
        onSubmit={(html, files) => onAddComment(html, files)}
      />
    </div>
  </Popover>
);

const optionShape = z.object({
  value: z.union([z.string(), z.number()]),
  label: z.string(),
  number: z.string().optional(),
});

const rowSchema = z.object({
  task_name: z.string().min(1, "Task name is required"),
  task_status: z.string().min(1),
  assignees: z.array(optionShape).optional(),
  priority: optionShape.nullable().optional(),
  start_date: asDateString("start date is required"),
  due_date: asDateString("end date is required"),
});

const TaskRow = ({
  task,
  assigneeOpts,
  headers,
  extras,
  customerId,
  projectData,
  currentUser,
  isEditing,
  onStartEdit,
  taskGrant,
  onSave,
  onCancelEdit,
  onOpenTask,
  onDeleteTask,
  onChangeStatus,
  isAdding,
}) => {
  const [openField, setOpenField] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentCount, setCommentCount] = useState(task?.comment_count ?? 0);
  const [isCommentOpen, setIsCommentOpen] = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const [statusAction, setStatusAction] = useState(null);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [showEditPopover, setShowEditPopover] = useState(false);
  const [editAnchorRect, setEditAnchorRect] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [rowDetailLoading, setRowDetailLoading] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [shareAnchorRect, setShareAnchorRect] = useState(null);
  const [shareMode, setShareMode] = useState("share"); // "share" | "whatsapp"
  const [showWhatsappPlatform, setShowWhatsappPlatform] = useState(false);
  const [whatsappAssignee, setWhatsappAssignee] = useState(null);
  const [whatsappAnchorRect, setWhatsappAnchorRect] = useState(null);
  const defaultValues = useMemo(
    () => ({
      task_name: task?.task_name || "",
      task_status: task?.task_status || "open",
      assignees: task?.assignees || [],
      priority:
        PRIORITY_OPTIONS.find((p) => p.value === task?.priority) || null,
      start_date: task?.start_date || todayISO(),
      due_date: task?.due_date || tomorrowISO(),
    }),
    [task],
  );

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
    watch,
    formState: { isDirty },
  } = useForm({
    resolver: zodResolver(rowSchema),
    defaultValues,
  });

  const watchedValues = watch();
  const hasChanges = isDirty;

  const isDirtyRef = useRef(isDirty);
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  // If the user starts editing, then changes everything back to the
  // original value, isDirty (and hence hasChanges/Save button) correctly
  // goes back to false. But isEditing/editingTaskId stays set, since that
  // is only cleared on explicit Save/Cancel — which then blocks other
  // actions ("Please save or cancel the task you are editing first.")
  // even though there is nothing left to save. Auto-exit edit mode the
  // moment isDirty flips back to false while this row is the active one.
  const wasDirtyRef = useRef(false);
  useEffect(() => {
    if (!isEditing) {
      wasDirtyRef.current = false;
      return;
    }
    if (isDirty) {
      wasDirtyRef.current = true;
    } else if (wasDirtyRef.current) {
      wasDirtyRef.current = false;
      onCancelEdit?.();
    }
  }, [isDirty, isEditing, onCancelEdit]);

  useEffect(() => {
    reset(defaultValues);
  }, [task?.id, defaultValues, reset]);

  const fetchComments = async () => {
    setCommentsLoading(true);
    try {
      const res = await GetRequest(
        `project/fetchTaskComment/${task?.id}`,
        { headers },
        extras,
      );
      if (res?.status === "success") {
        setComments(res.data || []);
        setCommentCount((res.data || []).length);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setCommentsLoading(false);
    }
  };

  const openComments = (e) => {
    if (!taskGrant?.isCreate)
      return toast.warning("You are not authorized for this action!");
    e.stopPropagation();
    setAnchorRect(e.currentTarget.getBoundingClientRect());
    setIsCommentOpen(true);
    setOpenField("comments");
    fetchComments();
  };

  const closeComments = () => {
    setIsCommentOpen(false);
    setOpenField(null);
    setAnchorRect(null);
  };

  const addComment = async (html, files = []) => {
    const tempId = `temp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      comment: html,
      user_name: currentUser.name,
      user_image: currentUser.image,
      user_login_id: currentUser.id,
      created_at: new Date().toISOString(),
      attachment_names: files.map((f) => f.name),
      _optimistic: true,
    };
    setComments((prev) => [...prev, optimistic]);
    setCommentCount((c) => c + 1);
    try {
      const res = await PostRequest(
        "project/insertTaskComment",
        {
          task_id: task?.id,
          comment: html,
        },
        false,
        { headers },
        extras,
      );
      if (!res || res.status === "fail") {
        setComments((prev) => prev.filter((c) => c.id !== tempId));
        setCommentCount((c) => c - 1);
        return toast.error(res?.message || "Could not post comment");
      }

      fetchComments();
    } catch (error) {
      console.error(error);
      setComments((prev) => prev.filter((c) => c.id !== tempId));
      setCommentCount((c) => c - 1);
      toast.error("Could not post comment");
    }
  };

  const onFormError = (errors) => {
    if (errors?.due_date) {
      toast.error(errors.due_date.message || "End date is required");
    } else if (errors?.start_date) {
      toast.error(errors.start_date.message || "Start date is required");
    }
  };

  const onSaveClick = async (values) => {
    if (!taskGrant?.isEdit)
      return toast.warning("You are not authorized for this action!");
    setSubmitting(true);
    try {
      const payload = {
        task_id: task?.id,
        project_id: projectData?.id,
        stage_id: task?.project_stage_id,
        task_name: values.task_name,
        task_status: values.task_status,
        priority: values.priority?.value || null,
        owner_id: parseOwnerId(values.assignees),
        start_date: values.start_date || todayISO(),
        end_date: values.due_date || tomorrowISO(),
        tags: task?.tags,
        description: task?.description || "",
      };

      await onSave(task, payload);
    } catch (error) {
      console.error(error);
      toast.error("Could not update task");
    } finally {
      setSubmitting(false);
    }
  };

  const onCancelClick = () => {
    reset(defaultValues);
    onCancelEdit();
  };

  const fetchFullTaskDetailForRow = async () => {
    setRowDetailLoading(true);
    try {
      const res = await GetRequest(
        `project/getDetailsOfTaskForEdit/${task?.id}/${customerId}/${currentUser?.id}`,
        { headers },
        extras,
      );
      const taskData = res?.data;
      if (!taskData) return;

      let parsedAssignees = [];
      if (taskData?.owner_id) {
        try {
          parsedAssignees =
            typeof taskData.owner_id === "string"
              ? JSON.parse(taskData.owner_id)
              : taskData.owner_id;
        } catch (e) {
          console.error("Failed to parse owner_id:", e);
        }
      }

      const matchedPriority =
        PRIORITY_OPTIONS.find(
          (p) => p.value?.toLowerCase() === taskData?.priority?.toLowerCase(),
        ) || null;

      const resolvedStartDate = taskData?.start_date || todayISO();
      if (isDirtyRef.current) return;
      reset({
        task_name: taskData?.task_name || "",
        task_status: taskData?.task_status || "open",
        assignees: parsedAssignees,
        priority: matchedPriority,
        start_date: resolvedStartDate,
        due_date:
          taskData?.end_date ||
          taskData?.due_date ||
          toISO(addDays(resolvedStartDate, 1)),
      });
    } catch (error) {
      console.error(error);
    } finally {
      setRowDetailLoading(false);
    }
  };

  const handleFieldChange = () => {
    if (isAdding) {
      toast.warning("Please save or cancel the new task first.");
      return;
    }
    if (!isEditing) {
      onStartEdit(task.id);
      fetchFullTaskDetailForRow();
    }
  };

  const handleEditClick = () => {
    if (isAdding) {
      toast.warning("Please save or cancel the new task first.");
      setShowMoreOptions(false);
      return;
    }
    // Capture the anchor rect before MoreOptionsPopover's onClose clears it.
    setEditAnchorRect(anchorRect);
    setShowEditPopover(true);
    setShowMoreOptions(false);
  };

  const closeEditPopover = () => {
    setShowEditPopover(false);
    setEditAnchorRect(null);
  };

  const handleDeleteClick = () => {
    if (
      window.confirm(
        `Are you sure you want to delete task "${task.task_name}"?`,
      )
    ) {
      onDeleteTask(task.id);
    }
    setShowMoreOptions(false);
  };

  const buildTaskMessage = (assignee) => {
    const crmLink = window.location.origin;
    return `*Task*
📄 *Task Name:* ${task?.task_name || "-"}
📄 *Assignee:* ${assignee?.label || "-"}
📅 *Start Date:* ${task?.start_date || new Date().toLocaleDateString()}
📅 *End Date:* ${task?.end_date || new Date().toLocaleDateString()}
🔗 *CRM Link:* ${crmLink}

Thank You`;
  };

  const getWhatsappPhone = (assignee) => {
    let phone = String(assignee?.number || "").replace(/\D/g, "");
    if (phone.length === 10) phone = `91${phone}`;
    return phone;
  };

  const getAssigneeEmail = (assignee) => {
    const fromOpts = (assigneeOpts || []).find(
      (o) => String(o.value) === String(assignee?.value),
    );
    return (assignee?.email || fromOpts?.email || "").trim();
  };

  const warnNoPhone = (assignee) =>
    toast.warning(
      `No phone number found for ${assignee?.label || "this assignee"}.`,
    );

  // "Share" -> native share sheet only (Teams / Outlook / any app user picks).
  const sendWhatsappToAssignee = async (assignee) => {
    if (!getWhatsappPhone(assignee)) {
      warnNoPhone(assignee);
      return;
    }

    if (!navigator.share) {
      toast.warning("Sharing is not supported in this browser.");
      return;
    }

    const message = buildTaskMessage(assignee);

    // Teams share sheet se sirf link leta hai, text ignore karta hai.
    // Isliye message copy kar do, user chat me paste kar sakta hai.
    navigator.clipboard
      ?.writeText(message)
      .then(() =>
        toast.info("Message copied. Agar app me text na aaye to paste kar do."),
      )
      .catch(() => { });

    try {
      await navigator.share({
        title: task?.task_name || "Task",
        text: message,
      });
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Sharing failed:", error);
        toast.warning("Unable to share.");
      }
    }
  };

  const openWhatsappChat = (assignee, platform) => {
    const message = buildTaskMessage(assignee);

    // WhatsApp Web / Desktop
    if (platform === "web" || platform === "desktop") {
      const phone = getWhatsappPhone(assignee);
      if (!phone) {
        warnNoPhone(assignee);
        return;
      }
      const text = encodeURIComponent(message);
      if (platform === "web") {
        window.open(
          `https://web.whatsapp.com/send?phone=${phone}&text=${text}`,
          "_blank",
          "noopener,noreferrer",
        );
      } else {
        // Opens the installed WhatsApp Desktop app (browser may ask permission).
        window.location.href = `whatsapp://send?phone=${phone}&text=${text}`;
      }
      return;
    }

    // Teams / Outlook / Email (these work with the assignee's email)
    const email = getAssigneeEmail(assignee);
    if (!email) {
      toast.warning(
        `No email found for ${assignee?.label || "this assignee"}.`,
      );
      return;
    }

    // WhatsApp *bold* stars don't render in these apps
    const plain = encodeURIComponent(message.replace(/\*/g, ""));
    const to = encodeURIComponent(email);
    const subject = encodeURIComponent(task?.task_name || "Task");

    if (platform === "teams") {
      const teamsBody = encodeURIComponent(
        `${message.replace(/\*/g, "")}\n📧 Email: ${email}`,
      );

      // Email clipboard me copy (silent) -> Teams me "To:" me Ctrl+V kar do
      navigator.clipboard?.writeText(email).catch(() => { });

      // Teams Desktop app direct open (browser "Open Microsoft Teams?" pooch sakta hai)
      window.location.href = `msteams:/l/chat/0/0?users=${to.replace(/%40/g, "@")}&message=${teamsBody}`;
    }
    else if (platform === "teams-personal") {
      const teamsBody = encodeURIComponent(
        `${message.replace(/\*/g, "")}\n📧 Email: ${email}`,
      );

      // Email clipboard me copy (silent) -> Teams me "To:" me Ctrl+V kar do
      navigator.clipboard?.writeText(email).catch(() => { });

      // Personal Teams (Microsoft account), browser me khulega
      window.open(
        `https://teams.live.com/l/chat/0/0?users=${to.replace(/%40/g, "@")}&message=${teamsBody}`,
        "_blank",
        "noopener,noreferrer",
      );
    } else if (platform === "outlook") {
      window.open(
        `https://outlook.office.com/mail/deeplink/compose?to=${to}&subject=${subject}&body=${plain}`,
        "_blank",
        "noopener,noreferrer",
      );
    } else {
      // default mail app
      window.location.href = `mailto:${email}?subject=${subject}&body=${plain}`;
    }
  };
  const openWhatsappPlatformPicker = (assignee, rect) => {
    setWhatsappAssignee(assignee);
    setWhatsappAnchorRect(rect);
    setShowWhatsappPlatform(true);
  };

  // Common flow for both buttons: pick assignee (if more than one) first.
  const startAssigneeFlow = (mode) => {
    const assignees = task?.assignees || [];

    if (assignees.length === 0) {
      toast.warning("No assignee found for this task!");
      setShowMoreOptions(false);
      return;
    }

    // Only one assignee -> no assignee popup needed.
    if (assignees.length === 1) {
      if (mode === "whatsapp") {
        openWhatsappPlatformPicker(assignees[0], anchorRect);
      } else {
        sendWhatsappToAssignee(assignees[0]);
      }
      setShowMoreOptions(false);
      return;
    }

    // Multiple assignees -> show popup to pick one.
    // Capture the anchor rect before MoreOptionsPopover's onClose clears it.
    setShareMode(mode);
    setShareAnchorRect(anchorRect);
    setShowShareOptions(true);
    setShowMoreOptions(false);
  };

  const handleShareClick = () => startAssigneeFlow("share");

  const handleWhatsappShare = () => startAssigneeFlow("whatsapp");

  const handleAssigneeClick = (e) => {
    e.stopPropagation();
    setAnchorRect(e.currentTarget.getBoundingClientRect());
    handleFieldChange();
    setOpenField("assignee");
  };

  const handleDateClick = (e) => {
    e.stopPropagation();
    setAnchorRect(e.currentTarget.getBoundingClientRect());
    handleFieldChange();
    setOpenField("date");
  };

  const handlePriorityClick = (e) => {
    e.stopPropagation();
    setAnchorRect(e.currentTarget.getBoundingClientRect());
    handleFieldChange();
    setOpenField("priority");
  };

  const handleStatusClick = (e) => {
    if (!taskGrant?.isEdit)
      return toast.warning("You are not authorized for this action!");
    e.stopPropagation();
    setAnchorRect(e.currentTarget.getBoundingClientRect());
    handleFieldChange();
    setOpenField("status");
  };

  const handleMoreClick = (e) => {
    e.stopPropagation();
    setAnchorRect(e.currentTarget.getBoundingClientRect());
    setShowMoreOptions(!showMoreOptions);
  };

  const closePopover = () => {
    setOpenField(null);
    setAnchorRect(null);
  };

  const handleStatusSubmit = async (reason) => {
    if (!statusAction) return;
    setStatusSubmitting(true);
    try {
      const ok = await onChangeStatus?.(task.id, {
        task_id: task.id,
        task_status: statusAction.value,
        ...(reason !== undefined ? { reason } : {}),
      });
      if (ok !== false) setStatusAction(null);
    } finally {
      setStatusSubmitting(false);
    }
  };

  return (
    <div className={`tl-row ${isEditing ? "tl-row-editing" : ""}`}>
      <div className="tl-cell tl-cell-name">
        <span
          className="tl-row-radio"
          style={{
            borderColor: (
              STATUS_OPTIONS.find(
                (s) => s.value === watchedValues.task_status,
              ) || STATUS_OPTIONS[0]
            ).color,
          }}
        />
        <Controller
          name="task_name"
          control={control}
          render={({ field }) => (
            <input
              className="tl-name-input"
              value={field.value}
              onChange={(e) => {
                field.onChange(e);
                handleFieldChange();
              }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        />
        {onOpenTask && (
          <span
            className="tl-open-task"
            onClick={() => onOpenTask(task)}
            title="View task details"
          >
            <FaEye size={12} />
          </span>
        )}
        {rowDetailLoading && <span className="tl-row-loading-spinner" />}
      </div>

      <div
        className="tl-cell tl-cell-assignee"
        style={{ position: "relative" }}
      >
        <Controller
          name="assignees"
          control={control}
          render={({ field }) => (
            <>
              <div className="tl-assignee-stack" onClick={handleAssigneeClick}>
                {(field.value || []).length === 0 && (
                  <span className="tl-add-assignee">
                    <FaUser size={10} />
                  </span>
                )}
                {/* {(field.value || []).map((a) => (
                  <Avatar key={a.value} name={a.label} id={a.value} size={24} />
                ))} */}
                {(field.value || []).slice(0, 3).map((a) => (
                  <Avatar key={a.value} name={a.label} id={a.value} size={35} />
                ))}
                {(field.value || []).length > 3 && (
                  <span className="tl-assignee-more">
                    +{(field.value || []).length - 3}
                  </span>
                )}
              </div>
              {openField === "assignee" && (
                <AssigneePopover
                  options={assigneeOpts}
                  selected={field.value || []}
                  onChange={(sel) => {
                    field.onChange(sel);
                    handleFieldChange();
                  }}
                  onClose={closePopover}
                  anchorRect={anchorRect}
                />
              )}
            </>
          )}
        />
      </div>

      <div className="tl-cell tl-cell-date" style={{ position: "relative" }}>
        <Controller
          name="due_date"
          control={control}
          render={({ field }) => (
            <>
              <span
                className={`tl-date-pill ${isToday(field.value) ? "tl-date-today" : ""} ${field.value && isOverdue(field.value) && !isToday(field.value)
                  ? "tl-date-overdue"
                  : ""
                  }`}
                onClick={handleDateClick}
              >
                <FaRegCalendarAlt size={10} className="me-1" />
                {field.value
                  ? isToday(field.value)
                    ? "Today"
                    : isYesterday(field.value)
                      ? "Yesterday"
                      : formatDateInAlphabeticalMonth(field.value)
                  : "Due date"}
              </span>
              {openField === "date" && (
                <DatePopover
                  startDate={getValues("start_date")}
                  dueDate={field.value}
                  activeField="due_date"
                  onChange={(patch) => {
                    if (patch.due_date !== undefined)
                      field.onChange(patch.due_date);
                    if (patch.start_date !== undefined) {
                      setValue("start_date", patch.start_date);
                      // Keep end date one day after the newly picked start date.
                      field.onChange(toISO(addDays(patch.start_date, 1)));
                    }
                    handleFieldChange();
                  }}
                  onClose={closePopover}
                  anchorRect={anchorRect}
                />
              )}
            </>
          )}
        />
      </div>

      <div
        className="tl-cell tl-cell-priority"
        style={{ position: "relative" }}
      >
        <Controller
          name="priority"
          control={control}
          render={({ field }) => (
            <>
              <span
                className="tl-flag-btn"
                style={{ color: field.value?.color || "#5c5c66" }}
                onClick={handlePriorityClick}
              >
                <FaFlag size={12} />
                {field.value?.label && (
                  <span className="tl-flag-label">{field.value.label}</span>
                )}
              </span>
              {openField === "priority" && (
                <OptionPopover
                  options={PRIORITY_OPTIONS}
                  allowClear
                  onSelect={(opt) => {
                    field.onChange(opt);
                    closePopover();
                    handleFieldChange();
                  }}
                  onClose={closePopover}
                  anchorRect={anchorRect}
                />
              )}
            </>
          )}
        />
      </div>
      {/* 
      <div className="tl-cell tl-cell-status" style={{ position: "relative" }}>
        <Controller
          name="task_status"
          control={control}
          render={({ field }) => {
            const opt =
              STATUS_OPTIONS.find((s) => s.value === field.value) ||
              STATUS_OPTIONS[0];
            return (
              <>
                <span
                  className="tl-status-pill"
                  style={{ background: opt.color }}
                  onClick={handleStatusClick}
                >
                  <span className="tl-status-pill-dot" />
                  {opt.label}
                </span>
                {openField === "status" && (
                  <OptionPopover
                    options={STATUS_ACTION_OPTIONS}
                    onSelect={(opt2) => {
                      closePopover();
                      setStatusAction(opt2);
                    }}
                    onClose={closePopover}
                    anchorRect={anchorRect}
                  />
                )}
                {statusAction && (
                  <ChangeStatusModal
                    status={statusAction}
                    submitting={statusSubmitting}
                    onCancel={() => setStatusAction(null)}
                    onSubmit={handleStatusSubmit}
                  />
                )}
              </>
            );
          }}
        />
      </div> */}
      <div className="tl-cell tl-cell-status" style={{ position: "relative" }}>
        <Controller
          name="task_status"
          control={control}
          render={({ field }) => {
            const opt =
              STATUS_OPTIONS.find((s) => s.value === field.value) ||
              STATUS_OPTIONS[0];

            // Current status value check
            const currentVal = String(field.value || "")
              .toLowerCase()
              .trim();
            const filteredActionOptions = STATUS_ACTION_OPTIONS.filter((o) => {
              const optionVal = String(o.value || o.label || "")
                .toLowerCase()
                .trim();
              return optionVal !== currentVal;
            });

            return (
              <>
                <span
                  className="tl-status-pill"
                  style={{ background: opt.color }}
                  onClick={handleStatusClick}
                >
                  <span className="tl-status-pill-dot" />
                  {opt.label}
                </span>
                {openField === "status" && (
                  <OptionPopover
                    options={filteredActionOptions}
                    onSelect={(opt2) => {
                      closePopover();
                      setStatusAction(opt2);
                    }}
                    onClose={closePopover}
                    anchorRect={anchorRect}
                  />
                )}
                {statusAction && (
                  <ChangeStatusModal
                    status={statusAction}
                    submitting={statusSubmitting}
                    onCancel={() => setStatusAction(null)}
                    onSubmit={handleStatusSubmit}
                  />
                )}
              </>
            );
          }}
        />
      </div>
      <div
        className="tl-cell tl-cell-comments"
        style={{ position: "relative" }}
      >
        <span className="tl-comment-count" onClick={openComments}>
          <FaRegCommentDots size={12} className="me-1" />
          {commentCount === 0 ? task?.comments : commentCount}
        </span>
        {isCommentOpen && (
          <CommentThreadPopover
            task={task}
            comments={comments}
            loading={commentsLoading}
            currentUser={currentUser}
            TaskRow={taskGrant}
            assigneeOptions={assigneeOpts}
            onAddComment={addComment}
            onClose={closeComments}
            anchorRect={anchorRect}
          />
        )}
      </div>

      <div className="tl-cell tl-cell-more" style={{ position: "relative" }}>
        <span className="tl-more-btn" onClick={handleMoreClick}>
          <FaEllipsisH size={12} />
        </span>
        {showMoreOptions && (
          <MoreOptionsPopover
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onShare={handleShareClick}
            onWhatsappShare={handleWhatsappShare}
            taskGrant={taskGrant}
            onClose={() => {
              setShowMoreOptions(false);
              setAnchorRect(null);
            }}
            anchorRect={anchorRect}
          />
        )}
        {showShareOptions && (
          <ShareAssigneePopover
            assignees={task?.assignees || []}
            onSelect={(assignee) =>
              shareMode === "whatsapp"
                ? openWhatsappPlatformPicker(assignee, shareAnchorRect)
                : sendWhatsappToAssignee(assignee)
            }
            onClose={() => {
              setShowShareOptions(false);
              setShareAnchorRect(null);
            }}
            anchorRect={shareAnchorRect}
          />
        )}
        {showWhatsappPlatform && (
          <WhatsappPlatformPopover
            assignee={whatsappAssignee}
            onSelect={(platform) => openWhatsappChat(whatsappAssignee, platform)}
            onClose={() => {
              setShowWhatsappPlatform(false);
              setWhatsappAssignee(null);
              setWhatsappAnchorRect(null);
            }}
            anchorRect={whatsappAnchorRect}
          />
        )}
        {showEditPopover && (
          <EditTaskPopover
            task_id={task?.id}
            customerId={customerId}
            assigneeOpts={assigneeOpts}
            taskGrant={taskGrant}
            user_login_id={currentUser?.id}
            onClose={closeEditPopover}
            onSave={onSave}
            headers={headers}
            extras={extras}
            anchorRect={editAnchorRect}
          />
        )}
      </div>

      {isEditing && (
        <div className="tl-row-actions">
          <button
            className="tl-btn-cancel-small"
            onClick={onCancelClick}
            title="Cancel"
            disabled={submitting}
          >
            <FaUndo size={12} />
          </button>
          {hasChanges && (
            <button
              className="tl-btn-save-small btn btn-default"
              onClick={() => {
                if (!taskGrant.isEdit)
                  return toast.warning(
                    "You are not authorized for this action!",
                  );
                handleSubmit(onSaveClick, onFormError)();
              }}
              title="Save"
              disabled={submitting}
            >
              {submitting ? (
                <span className="tl-btn-spinner" />
              ) : (
                <FaCheck size={12} />
              )}
              <span>{submitting ? "Saving..." : "Save"}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const AddTaskRow = ({
  stage,
  assigneeOpts,
  onCancel,
  onCreated,
  projectData,
  taskGrant,
  headers,
  extras,
  createTaskEndpoint,
}) => {
  const [openField, setOpenField] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const inputRef = useRef(null);

  const { control, handleSubmit, getValues, setValue } = useForm({
    resolver: zodResolver(addTaskSchema),
    defaultValues: {
      task_name: "",
      assignees: [],
      priority: null,
      start_date: todayISO(),
      due_date: tomorrowISO(),
    },
  });

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const onFormError = (errors) => {
    if (errors?.due_date) {
      toast.error(errors.due_date.message || "End date is required");
    } else if (errors?.start_date) {
      toast.error(errors.start_date.message || "Start date is required");
    }
  };

  const onSubmit = async (values) => {
    try {
      const payload = {
        project_id: projectData?.id,
        project_stage_id: stage?.value,
        task_name: values.task_name,
        task_status: "open",
        priority: values.priority?.value || null,
        owner_id: parseOwnerId(values.assignees),
        start_date: values.start_date || todayISO(),
        end_date: values.due_date || tomorrowISO(),
        tags: [],
        description: "",
      };
      const res = await PostRequest(
        `project/createTask`,
        payload,
        false,
        { headers },
        extras,
      );
      if (!res || res.status === "fail") {
        return toast.error(res?.message || "Could not create task");
      }
      onCreated?.(res.data);
    } catch (error) {
      console.error(error);
      toast.error("Could not create task");
    }
  };

  const handleAssigneeClick = (e) => {
    e.stopPropagation();
    setAnchorRect(e.currentTarget.getBoundingClientRect());
    setOpenField("assignee");
  };

  const handleDateClick = (e) => {
    e.stopPropagation();
    setAnchorRect(e.currentTarget.getBoundingClientRect());
    setOpenField("date");
  };

  const handlePriorityClick = (e) => {
    e.stopPropagation();
    setAnchorRect(e.currentTarget.getBoundingClientRect());
    setOpenField("priority");
  };

  const closePopover = () => {
    setOpenField(null);
    setAnchorRect(null);
  };

  return (
    <div className="tl-add-row">
      <Controller
        name="task_name"
        control={control}
        render={({ field }) => (
          <input
            {...field}
            ref={inputRef}
            className="tl-add-input"
            placeholder="Task Name or type '/' for commands"
            onKeyDown={(e) =>
              e.key === "Enter" && handleSubmit(onSubmit, onFormError)()
            }
          />
        )}
      />
      <div className="tl-add-row-icons">
        <span className="tl-add-icon-pill">
          <FaChevronDown size={9} className="me-1" />
          Task
        </span>

        <Controller
          name="assignees"
          control={control}
          render={({ field }) => (
            <span
              className="tl-add-icon"
              style={{ position: "relative" }}
              onClick={handleAssigneeClick}
            >
              <FaAt
                size={12}
                style={{
                  color:
                    field.value && field.value.length > 0
                      ? "#2563eb"
                      : "#6b7280",
                }}
              />
              {openField === "assignee" && (
                <AssigneePopover
                  options={assigneeOpts}
                  selected={field.value || []}
                  onChange={(sel) => field.onChange(sel)}
                  onClose={closePopover}
                  anchorRect={anchorRect}
                />
              )}
            </span>
          )}
        />

        <Controller
          name="due_date"
          control={control}
          render={({ field }) => (
            <span
              className="tl-add-icon"
              style={{ position: "relative" }}
              onClick={handleDateClick}
            >
              <FaRegCalendarAlt
                size={12}
                s
                style={{
                  color:
                    field.value && field.value.length > 0
                      ? "#2563eb"
                      : "#6b7280",
                }}
              />
              {openField === "date" && (
                <DatePopover
                  startDate={getValues("start_date")}
                  dueDate={field.value}
                  activeField="due_date"
                  onChange={(patch) => {
                    if (patch.due_date !== undefined)
                      field.onChange(patch.due_date);
                    if (patch.start_date !== undefined) {
                      setValue("start_date", patch.start_date);
                      // Keep end date one day after the newly picked start date.
                      field.onChange(toISO(addDays(patch.start_date, 1)));
                    }
                  }}
                  onClose={closePopover}
                  anchorRect={anchorRect}
                />
              )}
            </span>
          )}
        />

        <Controller
          name="priority"
          control={control}
          render={({ field }) => (
            <span
              className="tl-add-icon"
              style={{ position: "relative" }}
              onClick={handlePriorityClick}
            >
              <FaFlag size={12} style={{ color: field.value?.color }} />
              {openField === "priority" && (
                <OptionPopover
                  options={PRIORITY_OPTIONS}
                  allowClear
                  onSelect={(opt) => {
                    field.onChange(opt);
                    closePopover();
                  }}
                  onClose={closePopover}
                  anchorRect={anchorRect}
                />
              )}
            </span>
          )}
        />
      </div>

      <div className="tl-add-row-actions">
        <button type="button" className="tl-btn-cancel" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="tl-btn-save"
          onClick={handleSubmit(onSubmit, onFormError)}
        >
          Save ⏎
        </button>
      </div>
    </div>
  );
};
const addTaskSchema = z.object({
  task_name: z.string().min(1, "Task name is required"),
  assignees: z.array(optionShape).optional(),
  priority: optionShape.nullable().optional(),
  start_date: asDateString("start date is required"),
  due_date: asDateString("end date is required"),
});

const StageGroup = ({
  stage,
  stageIndex,
  taskGrant,
  tasks,
  assigneeOpts,
  currentUser,
  projectData,
  headers,
  extras,
  customerId,
  editingTaskId,
  onStartEdit,
  onSaveTask,
  onCancelEdit,
  onTaskCreated,
  onOpenTask,
  onDeleteTask,
  onChangeStatus,
  createTaskEndpoint,
  statusFilter,
  priorityFilter,
  addingStageId,
  onOpenAdd,
  onCloseAdd,
}) => {
  const [collapsed, setCollapsed] = useState(tasks.length === 0);
  const adding = addingStageId === stage.id;
  const isAddingAnyTask = !!addingStageId;
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const toggleSort = (field) => {
    if (sortField !== field) {
      setSortField(field);
      setSortDir("asc");
    } else {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    }
  };

  const sortedTasks = useMemo(() => {
    if (!sortField) return tasks;
    const list = [...tasks];
    if (sortField === "date") {
      list.sort((a, b) => {
        const at = a.due_date ? new Date(a.due_date).getTime() : null;
        const bt = b.due_date ? new Date(b.due_date).getTime() : null;
        // Tasks without a due date always sort to the end.
        if (at === null && bt === null) return 0;
        if (at === null) return 1;
        if (bt === null) return -1;
        return sortDir === "asc" ? at - bt : bt - at;
      });
    } else if (sortField === "status") {
      list.sort((a, b) => {
        const aLabel = (
          STATUS_OPTIONS.find((s) => s.value === a.task_status)?.label ||
          a.task_status ||
          ""
        ).toLowerCase();
        const bLabel = (
          STATUS_OPTIONS.find((s) => s.value === b.task_status)?.label ||
          b.task_status ||
          ""
        ).toLowerCase();
        if (aLabel < bLabel) return sortDir === "asc" ? -1 : 1;
        if (aLabel > bLabel) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    } else if (sortField === "priority") {
      list.sort((a, b) => {
        const aLabel = (
          PRIORITY_OPTIONS.find((p) => p.value === a.priority)?.label ||
          a.priority ||
          ""
        ).toLowerCase();
        const bLabel = (
          PRIORITY_OPTIONS.find((p) => p.value === b.priority)?.label ||
          b.priority ||
          ""
        ).toLowerCase();
        if (aLabel < bLabel) return sortDir === "asc" ? -1 : 1;
        if (aLabel > bLabel) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return list;
  }, [tasks, sortField, sortDir]);
  const userToggledRef = useRef(false);

  useEffect(() => {
    if (!userToggledRef.current) {
      setCollapsed(tasks.length === 0);
    }
  }, [tasks.length]);

  const stageName = stage.stage_name || stage.label || "";
  const openType = isOpenStage(stageName);
  const badgeColor = getStageBadgeColor(stage, stageIndex ?? 0);

  return (
    <div className="tl-stage-group">
      <div
        className="tl-stage-header"
        onClick={() => {
          userToggledRef.current = true;
          setCollapsed((c) => {
            const willCollapse = !c;
            // If we're collapsing this stage while one of its tasks is mid-edit,
            // the save/cancel row action becomes hidden, so clear the edit
            // state too — otherwise editingTaskId stays set and blocks other
            // actions (e.g. "Add Task") with no visible way to resolve it.
            if (
              willCollapse &&
              editingTaskId &&
              tasks.some((t) => t.id === editingTaskId)
            ) {
              onCancelEdit?.();
            }
            return willCollapse;
          });
        }}
      >
        <div className="tl-stage-header-left">
          {collapsed ? (
            <FaChevronRight size={11} />
          ) : (
            <FaChevronDown size={11} />
          )}
          {openType ? (
            <span
              className="tl-stage-label-plain"
              style={{ fontSize: "16px", fontWeight: "500" }}
            >
              <span className="tl-stage-ring" />
              {stageName}
            </span>
          ) : (
            //className="tl-stage-badge" style={{ background: badgeColor }}
            <span style={{ fontSize: "16px", fontWeight: "500" }}>
              <span className="tl-stage-badge-dot" />
              {stageName}
            </span>
          )}
          <span className="tl-muted fs-12">Task Count : {tasks.length}</span>
        </div>
        <div className="tl-stage-header-right">
          <span
            className="tl-stage-add-icon"
            title="Add task"
            onClick={(e) => {
              if (!taskGrant?.isCreate)
                return toast.warning("You are not authorized for this action!");
              e.stopPropagation();
              if (editingTaskId) {
                toast.warning(
                  "Please save or cancel the task you are editing first.",
                );
                return;
              }
              userToggledRef.current = true;
              setCollapsed(false);
              onOpenAdd(stage.id);
            }}
          >
            <FaPlus size={11} />
          </span>
        </div>
      </div>

      {!collapsed && (
        <>
          <div className="tl-table-head">
            <span>Name</span>
            <span className="tl-cell-assignee">Assignee</span>
            <span
              className="tl-cell-name tl-th-clickable"
              title="Sort by due date"
              onClick={() => toggleSort("date")}
            >
              Due date
              {sortField === "date" &&
                (sortDir === "asc" ? (
                  <span className="tl-th-sort-label tl-th-sort-label-active">
                    A-Z <FaArrowUp size={10} />
                  </span>
                ) : (
                  <span className="tl-th-sort-label tl-th-sort-label-active">
                    Z-A <FaArrowDown size={10} />
                  </span>
                ))}
            </span>
            <span
              className="tl-cell-priority tl-th-clickable"
              title="Sort by priority"
              onClick={() => toggleSort("priority")}
            >
              Priority
              {priorityFilter && <span className="tl-th-filter-dot" />}
              {sortField === "priority" &&
                (sortDir === "asc" ? (
                  <span className="tl-th-sort-label tl-th-sort-label-active">
                    A-Z <FaArrowUp size={10} />
                  </span>
                ) : (
                  <span className="tl-th-sort-label tl-th-sort-label-active">
                    Z-A <FaArrowDown size={10} />
                  </span>
                ))}
            </span>
            <span
              className="tl-cell-status tl-th-clickable"
              title="Sort by status"
              onClick={() => toggleSort("status")}
            >
              Status
              {statusFilter && <span className="tl-th-filter-dot" />}
              {sortField === "status" &&
                (sortDir === "asc" ? (
                  <span className="tl-th-sort-label tl-th-sort-label-active">
                    A-Z <FaArrowUp size={10} />
                  </span>
                ) : (
                  <span className="tl-th-sort-label tl-th-sort-label-active">
                    Z-A <FaArrowDown size={10} />
                  </span>
                ))}
            </span>
            <span className="tl-cell-comments">Comments</span>
            <span className="tl-cell-more" />
          </div>

          {sortedTasks.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              assigneeOpts={assigneeOpts}
              headers={headers}
              extras={extras}
              customerId={customerId}
              projectData={projectData}
              currentUser={currentUser}
              taskGrant={taskGrant}
              isEditing={editingTaskId === t.id}
              onStartEdit={onStartEdit}
              onSave={onSaveTask}
              onCancelEdit={onCancelEdit}
              onOpenTask={onOpenTask}
              onDeleteTask={onDeleteTask}
              onChangeStatus={onChangeStatus}
              isAdding={isAddingAnyTask}
              count={3}
            />
          ))}

          {/* <div 
            className="tl-stage-add-bottom"
            onClick={() => setAdding(true)}
          >
            <FaPlus size={10} className="me-1" /> Add Task
          </div> */}

          {adding && (
            <AddTaskRow
              stage={{ value: stage.id, label: stage.stage_name }}
              assigneeOpts={assigneeOpts}
              projectData={projectData}
              headers={headers}
              taskGrant={taskGrant}
              extras={extras}
              createTaskEndpoint={createTaskEndpoint}
              onCancel={onCloseAdd}
              onCreated={(newTask) => {
                onCloseAdd();
                onTaskCreated?.({ ...newTask, project_stage_id: stage.id });
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export const ProjectTaskListView = ({
  projectData,
  headers,
  extras,
  customerId,
  customer,
  onOpenTask,
  onDataChange,
  taskGrant,
  createTaskEndpoint = "project/createTask",
}) => {
  const [tasks, setTasks] = useState([]);
  const [assigneeOpts, setAssigneeOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [addingStageId, setAddingStageId] = useState(null);

  // Status/Priority filters are mutually exclusive - only one applies at a time.
  const [statusFilter, setStatusFilter] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState(null);
  const [openHeaderFilter, setOpenHeaderFilter] = useState(null); // "status" | "priority" | null
  const [headerFilterAnchorRect, setHeaderFilterAnchorRect] = useState(null);

  const handleOpenHeaderFilter = (type, rect) => {
    setHeaderFilterAnchorRect(rect);
    setOpenHeaderFilter((prev) => (prev === type ? null : type));
  };

  const closeHeaderFilter = () => {
    setOpenHeaderFilter(null);
    setHeaderFilterAnchorRect(null);
  };

  const handleSelectStatusFilter = (opt) => {
    setStatusFilter(opt ? opt.value : null);
    setPriorityFilter(null);
    closeHeaderFilter();
  };

  const handleSelectPriorityFilter = (opt) => {
    setPriorityFilter(opt ? opt.value : null);
    setStatusFilter(null);
    closeHeaderFilter();
  };

  const stages = useMemo(() => {
    if (!projectData?.stages) return [];
    return projectData.stages;
  }, [projectData]);

  const currentUser = useMemo(
    () => ({
      id: extras?.customer?.customer_details?.id,
      name:
        extras?.customer?.customer_details?.full_name ||
        extras?.customer?.customer_details?.name ||
        "You",
      image:
        extras?.customer?.customer_details?.profile_image ||
        extras?.customer?.customer_details?.avatar ||
        null,
    }),
    [extras],
  );

  useEffect(() => {
    if (projectData?.stages) {
      const allTasks = [];
      projectData.stages.forEach((stage) => {
        if (stage.tasks) {
          stage.tasks.forEach((task) => {
            allTasks.push(
              normalizeTask({
                ...task,
                project_stage_id: stage.id,
                stage_name: stage.stage_name,
              }),
            );
          });
        }
      });
      setTasks(allTasks);
    }
  }, [projectData]);

  const fetchOwners = useCallback(async () => {
    try {
      const res = await GetRequest(
        `users/users/fetchUserAsOwner/${customerId}`,
        { headers },
        extras,
      );
      if (res?.err) return;
      setAssigneeOpts(
        (res?.data || []).map((x) => {
          const rawPhone =
            x.number || x.mobile_number || x.mobile || x.phone || "";
          return {
            value: x.id,
            label: x.full_name,
            number: toWhatsappNumber(rawPhone, x.country_code),
            email: x.email || x.email_id || x.user_email || x.official_email || "",
          };
        }),
      );
    } catch (error) {
      console.error(error);
    }
  }, [customerId, headers, extras]);

  useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  const visibleTasks = useMemo(() => {
    let list = tasks;
    if (statusFilter) list = list.filter((t) => t.task_status === statusFilter);
    if (priorityFilter)
      list = list.filter((t) => t.priority === priorityFilter);
    return list;
  }, [tasks, statusFilter, priorityFilter]);

  const tasksByStage = useMemo(() => {
    const map = {};
    stages.forEach((stage) => {
      map[stage.id] = [];
    });
    visibleTasks.forEach((t) => {
      const key = t.project_stage_id;
      if (map[key]) {
        map[key].push(t);
      }
    });
    return map;
  }, [stages, visibleTasks]);

  const handleStartEdit = (taskId) => {
    if (addingStageId) {
      toast.warning("Please save or cancel the new task first.");
      return;
    }
    if (editingTaskId && editingTaskId !== taskId) {
      setEditingTaskId(null);
    }
    setEditingTaskId(taskId);
  };

  const handleCancelEdit = () => {
    setEditingTaskId(null);
  };

  const handleSaveTask = async (task, payload) => {
    try {
      const parseIfNeeded = (data) => {
        if (typeof data === "string") {
          try {
            return JSON.parse(data);
          } catch (e) {
            return data;
          }
        }
        return data;
      };
      const previousPayload = {
        task_id: task?.id,
        project_id: task?.project_id,
        stage_id: task?.project_stage_id,
        task_name: task?.task_name,
        task_status: task?.task_status,
        priority: task?.priority,
        owner_id: parseIfNeeded(task?.owner_id),
        start_date: task?.start_date || null,
        end_date: task?.end_date || null,
        description: task?.description,
        tags: task?.tags || [],
      };
      payload.updating_field = getUpdatedFields(previousPayload, payload);
      const res = await PutRequest(
        "project/updateStageTask",
        payload,
        { headers },
        extras,
      );
      if (!res || res.status === "fail") {
        toast.error(res?.message || "Could not update task");
        return;
      }

      setTasks((prev) =>
        prev.map((t) =>
          t.id === task?.id
            ? normalizeTask(
              res?.data
                ? { ...t, ...res.data }
                : {
                  ...t,
                  task_name: payload.task_name,
                  task_status: payload.task_status,
                  priority: payload.priority,
                  owner_id: payload.owner_id,
                  start_date: payload.start_date,
                  end_date: payload.end_date,
                },
            )
            : t,
        ),
      );
      toast.success("Task updated successfully");
      getUpdatedFields({}, {});
      setEditingTaskId(null);
      onDataChange?.();
    } catch (error) {
      console.error(error);
      toast.error("Could not update task");
    }
  };

  const handleChangeTaskStatus = async (taskId, payload) => {
    try {
      const res = await PutRequest(
        "project/changeTaskStatus",
        payload,
        { headers },
        extras,
      );
      if (!res || res.status === "fail") {
        toast.error(res?.message || "Could not update task status");
        return false;
      }

      setTasks((prev) =>
        prev.map((t) =>
          t.id === taskId
            ? normalizeTask(
              res?.data
                ? { ...t, ...res.data }
                : { ...t, task_status: payload.status },
            )
            : t,
        ),
      );

      toast.success("Task status updated successfully");
      onDataChange?.();
      return true;
    } catch (error) {
      console.error(error);
      toast.error("Could not update task status");
      return false;
    }
  };

  const handleTaskCreated = (newTask) => {
    setTasks((prev) => [...prev, normalizeTask(newTask)]);
    toast.success("Task created successfully");
    onDataChange?.();
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const res = await PostRequest(
        `project/deleteTask/${taskId}`,
        {},
        false,
        { headers },
        extras,
      );
      if (res?.status === "success") {
        setTasks((prev) => prev.filter((t) => t.id !== taskId));
        toast.success("Task deleted successfully");
        onDataChange?.();
      } else {
        toast.error(res?.message || "Could not delete task");
      }
    } catch (error) {
      console.error(error);
      toast.error("Could not delete task");
    }
  };

  return (
    <div className="tl-wrap">
      {loading && <div className="tl-muted fs-13 p-3">Loading tasks…</div>}

      {!loading &&
        stages.map((stage, stageIndex) => (
          <StageGroup
            key={stage.id}
            stage={stage}
            stageIndex={stageIndex}
            tasks={tasksByStage[stage.id] || []}
            assigneeOpts={assigneeOpts}
            currentUser={currentUser}
            projectData={projectData}
            headers={headers}
            extras={extras}
            customerId={customerId}
            editingTaskId={editingTaskId}
            onStartEdit={handleStartEdit}
            onSaveTask={handleSaveTask}
            onCancelEdit={handleCancelEdit}
            onTaskCreated={handleTaskCreated}
            onOpenTask={onOpenTask}
            onDeleteTask={handleDeleteTask}
            onChangeStatus={handleChangeTaskStatus}
            createTaskEndpoint={createTaskEndpoint}
            statusFilter={statusFilter}
            priorityFilter={priorityFilter}
            openHeaderFilter={openHeaderFilter}
            headerFilterAnchorRect={headerFilterAnchorRect}
            onOpenHeaderFilter={handleOpenHeaderFilter}
            onCloseHeaderFilter={closeHeaderFilter}
            onSelectStatusFilter={handleSelectStatusFilter}
            onSelectPriorityFilter={handleSelectPriorityFilter}
            taskGrant={taskGrant}
            addingStageId={addingStageId}
            onOpenAdd={(stageId) => setAddingStageId(stageId)}
            onCloseAdd={() => setAddingStageId(null)}
          />
        ))}

      {!loading && stages.length === 0 && (
        <div className="tl-muted fs-13 p-3">
          No stages found for this project.
        </div>
      )}

      {openHeaderFilter === "priority" && (
        <OptionPopover
          options={PRIORITY_OPTIONS}
          onSelect={handleSelectPriorityFilter}
          onClose={closeHeaderFilter}
          allowClear
          anchorRect={headerFilterAnchorRect}
        />
      )}
      {openHeaderFilter === "status" && (
        <OptionPopover
          options={STATUS_OPTIONS}
          onSelect={handleSelectStatusFilter}
          onClose={closeHeaderFilter}
          allowClear
          anchorRect={headerFilterAnchorRect}
        />
      )}

      <style>{`
        /* ----- Light Theme (Standard Task List) ----- */
        .tl-wrap {
          color: #1a1a1e;
          font-size: 13px;
          padding: 16px 20px;
          background: #ffffff;
          min-height: 100vh;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
        }
        .tl-muted { color: #6b6b72; }
        .fw-semibold { font-weight: 600; }
        .fs-11 { font-size: 11px; }
        .fs-12 { font-size: 12px; }
        .fs-13 { font-size: 13px; }
        .me-1 { margin-right: 4px; }
        .ms-1 { margin-left: 4px; }
        .d-flex { display: flex; }
        .align-items-center { align-items: center; }
        .gap-2 { gap: 8px; }
        .flex-grow-1 { flex: 1; }
        .p-2 { padding: 8px; }
        .p-3 { padding: 12px; }
        .fst-italic { font-style: italic; }

        .tl-stage-group {
          margin-bottom: 20px;
          background: #ffffff;
          // border-radius: 6px;
          padding: 0 0 4px;
          // border: 1px solid #e4e4e7;
        }
        .tl-stage-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 16px;
          cursor: pointer;
          user-select: none;
          transition: background 0.2s;
          background: #ffffff;
        }
        // .tl-stage-header:hover { background: #f7f8fa; }
        .tl-stage-header-left {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .tl-stage-header-right {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .tl-stage-add-icon {
          color: white;
          background-color:#813d63;
          cursor: pointer;
          padding: 5px;
          border-radius: 50%;
          display: inline-flex;
          transition: all 0.2s;
        }
        .tl-stage-add-icon:hover { color: #000; background: #ececf0; }
        .tl-dot {
          width: 8px;
           color: white;
          background-color:#813d63;
          height: 8px;
          border-radius: 50%;
          display: inline-block;
        }
        .tl-stage-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #ffffff;
          font-weight: 700;
          font-size: 11px;
          letter-spacing: 0.4px;
          padding: 4px 12px;
          border-radius: 4px;
          text-transform: uppercase;
        }
        .tl-stage-badge-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #ffffff;
          box-shadow: 0 0 0 2px rgba(255,255,255,0.45);
          display: inline-block;
        }
        .tl-stage-label-plain {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #1a1a1e;
          font-weight: 700;
          font-size: 11px;
          letter-spacing: 0.4px;
          text-transform: uppercase;
        }
        .tl-stage-ring {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          border: 2px solid #a0a0a8;
          display: inline-block;
        }

        .tl-table-head {
          display: grid;
          grid-template-columns: 1fr 100px 120px 110px 130px 90px 40px;
          align-items: center;
          gap: 12px;
          padding: 16px 16px;
          color: #6b6b72;
          font-size: 11px;
          // text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 1px solid #7a3a5e;
          border-top:1px solid #7a3a5e;
          background: #f7f7f7;
          font-weight: 600;
        }
        .tl-th-clickable {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          cursor: pointer;
          user-select: none;
          transition: color 0.15s;
        }
        .tl-th-clickable:hover { color: #1a1a1e; }
        .tl-th-sort-idle { opacity: 0.35; }
        .tl-th-sort-label {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          font-size: 10px;
        }
        .tl-th-sort-label-active {
          background: #7B3FE4;
          color: #fff;
          padding: 2px 6px;
          border-radius: 10px;
        }
        .tl-th-filter-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #7B3FE4;
          display: inline-block;
        }
        .tl-ai-chip {
          display: inline-flex;
          align-items: center;
          font-size: 9px;
          font-weight: 700;
          color: #7B3FE4;
          background: #f1e9ff;
          border-radius: 3px;
          padding: 1px 4px;
          margin-left: 4px;
          letter-spacing: 0;
        }

        .tl-row {
          display: grid;
          grid-template-columns: 1fr 100px 120px 110px 130px 90px 40px;
          align-items: center;
          gap: 12px;
          padding: 4px 16px;
          min-height: 50px;
          border-bottom: 1px solid #eeeeef;
          position: relative;
          transition: background 0.15s;
          background: #ffffff;
        }
        .tl-row:hover { background: #f5f8fc; }
        .tl-row-editing {
          background: #e6f0ff !important;
          border-left: 3px solid #7B3FE4;
        }

        .tl-cell-name {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
        }
        .tl-row-radio {
          width: 15px;
          height: 15px;
          border-radius: 50%;
          border: 2.5px solid #aaa;
          flex-shrink: 0;
          cursor: pointer;
        }
        .tl-row-radio:hover { opacity: 0.75; }
        .tl-name-input {
          background: transparent;
          border: none;
          color: #1a1a1e;
          font-size: 13px;
          width: 100%;
          // max-width: 240px;
          padding: 4px 6px;
          border-radius: 4px;
        }
        .tl-name-input:focus {
          outline: none;
          background: #f0f0f0;
        }
        .tl-name-input:hover { background: #f0f0f0; }
        .tl-open-task {
          color: #888;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.2s;
          padding: 4px;
          flex-shrink: 0;
        }
        .tl-row:hover .tl-open-task { opacity: 1; }
        .tl-open-task:hover { color: #000; }

        .tl-assignee-stack {
          display: flex;
          align-items: center;
          gap: 0;
          cursor: pointer;
          min-height: 28px;
          padding: 2px 0;
        }
        .tl-assignee-stack > *:not(:first-child) {
          margin-left: -10px;
        }
        .tl-assignee-stack .tl-avatar,
        .tl-assignee-stack .tl-avatar-img {
          border: 2px solid #fff;
        }
        .tl-assignee-stack .tl-avatar:hover,
        .tl-assignee-stack .tl-avatar-img:hover {
          border-color: #7B3FE4;
          z-index: 1;
          position: relative;
        }
        .tl-add-assignee {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          border: 1px dashed #aaa;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #888;
          font-size: 11px;
          transition: all 0.2s;
        }
        .tl-add-assignee:hover {
          border-color: #555;
          color: #000;
        }

        .tl-avatar {
          border-radius: 50%;
          color: #fff;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 10px;
          border: 2px solid transparent;
          transition: border-color 0.2s;
        }
        .tl-avatar:hover { border-color: #7B3FE4; }
        .tl-avatar-img {
          border-radius: 50%;
          object-fit: cover;
          flex-shrink: 0;
          border: 2px solid transparent;
        }
        .tl-avatar-img:hover { border-color: #7B3FE4; }

        .tl-date-pill {
          display: inline-flex;
          align-items: center;
          font-size: 12px;
          color: #333;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 4px;
          transition: background 0.2s;
          white-space: nowrap;
        }
        .tl-date-pill:hover { background: #e8e8e8; }
        .tl-date-today {
          color: #f1c232;
          font-weight: 600;
        }
        .tl-date-overdue {
          color: #e5484d;
          font-weight: 600;
        }

        .tl-flag-btn {
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 6px;
          border-radius: 4px;
          transition: background 0.2s;
        }
        .tl-flag-btn:hover { background: #e8e8e8; }
        .tl-flag-label {
          font-size: 12px;
          font-weight: 500;
        }

        .tl-status-pill {
          border: none;
          border-radius: 40px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.3px;
          color: #ffffff;
          padding: 4px 10px 4px 8px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s;
          white-space: nowrap;
        }
        .tl-status-pill:hover { filter: brightness(1.08); }
        .tl-status-pill-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: rgba(255,255,255,0.9);
          box-shadow: 0 0 0 2px rgba(255,255,255,0.35);
          display: inline-block;
          flex-shrink: 0;
        }

        .tl-comment-count {
          display: inline-flex;
          align-items: center;
          color: #555;
          font-size: 12px;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 4px;
          transition: all 0.2s;
        }
        .tl-comment-count:hover {
          color: #000;
          background: #e8e8e8;
        }

        .tl-more-btn {
          color: #777;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 4px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }
        .tl-more-btn:hover {
          color: #000;
          background: #e8e8e8;
        }

        .tl-more-popover {
          min-width: 140px;
          right: 0;
          left: auto;
        }
        .tl-more-popover .tl-popover-row {
          padding: 8px 12px;
        }
        .tl-more-popover .tl-popover-row:hover {
          background: #e8e8e8;
        }
        .tl-more-popover .tl-danger {
          color: #e5484d;
        }
        .tl-more-popover .tl-danger:hover {
          background: #fce4e4;
        }

        .tl-edit-popover {
          width: 300px;
          min-width: 0;
          padding: 12px;
          right: 0;
          left: auto;
        }
        .tl-edit-popover-input {
          width: 100%;
          border: 1px solid #ccc;
          border-radius: 6px;
          background: #fff;
          color: #1a1a1e;
          font-size: 13px;
          padding: 8px 10px;
          margin-bottom: 10px;
          outline: none;
        }
        .tl-edit-popover-input:focus { border-color: #7B3FE4; }
        .tl-edit-popover-icons {
          display: flex;
          align-items: center;
          gap: 10px;
          padding-bottom: 10px;
          margin-bottom: 10px;
          border-bottom: 1px solid #eee;
        }
        .tl-edit-popover-actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }

        .tl-row-actions {
          position: absolute;
          right: 0px;
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          gap: 4px;
          background: #fff;
          padding: 4px 6px;
          border-radius: 6px;
          border: 1px solid #ccc;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }
        .tl-btn-cancel-small, .tl-btn-save-small {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 4px;
          border: none;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .tl-btn-cancel-small {
          background: transparent;
          color: #555;
        }
        .tl-btn-cancel-small:hover {
          background: #e8e8e8;
          color: #000;
        }
        .tl-btn-save-small {
          background: #7B3FE4;
          color: #fff;
        }
        .tl-btn-save-small:hover {
          background: #6a35cc;
          transform: scale(1.02);
        }
        .tl-btn-cancel-small:disabled,
        .tl-btn-save-small:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        .tl-btn-spinner {
          width: 12px;
          height: 12px;
          border: 2px solid rgba(255, 255, 255, 0.4);
          border-top-color: #fff;
          border-radius: 50%;
          display: inline-block;
          animation: tl-spin 0.6s linear infinite;
        }
        .tl-edit-popover-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 22px 10px;
          min-width: 220px;
        }
        .tl-loading-spinner {
          width: 18px;
          height: 18px;
          border: 2px solid #e0e2e7;
          border-top-color: #7B3FE4;
          border-radius: 50%;
          display: inline-block;
          animation: tl-spin 0.6s linear infinite;
        }
        .tl-row-loading-spinner {
          width: 13px;
          height: 13px;
          border: 2px solid #e0e2e7;
          border-top-color: #7B3FE4;
          border-radius: 50%;
          display: inline-block;
          animation: tl-spin 0.6s linear infinite;
          margin-left: 6px;
          flex-shrink: 0;
        }
        @keyframes tl-spin {
          to { transform: rotate(360deg); }
        }

        .tl-popover-backdrop {
          position: fixed;
          inset: 0;
          z-index: 999;
          background: rgba(0,0,0,0.1);
        }
        .tl-popover {
          position: fixed;
          z-index: 1000;
          background: #fff;
          border: 1px solid #ccc;
          border-radius: 8px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.2);
          min-width: 220px;
          padding: 6px;
          animation: tlPopoverIn 0.15s ease;
          max-height: 80vh;
          overflow-y: auto;
        }
        @keyframes tlPopoverIn {
          from { opacity: 0; transform: scale(0.95) translateY(-4px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .tl-popover-search {
          width: 100%;
          background: #f5f5f5;
          border: 1px solid #ccc;
          border-radius: 6px;
          color: #1a1a1e;
          font-size: 13px;
          padding: 6px 10px;
          margin-bottom: 6px;
          outline: none;
        }
        .tl-popover-search:focus { border-color: #7B3FE4; }
        .tl-popover-section-label {
          font-size: 10px;
          color: #666;
          padding: 4px 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .tl-popover-list { max-height: 220px; overflow-y: auto; }
        .tl-popover-row {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 8px;
          border-radius: 6px;
          cursor: pointer;
          transition: background 0.15s;
        }
        .tl-popover-row:hover { background: #f0f0f0; }
        .tl-popover-row-active { background: #e0ecff; }
        .tl-popover-empty {
          padding: 8px;
          color: #666;
          font-size: 12px;
        }
        .tl-check { margin-left: auto; color: #4592f0; }
        .tl-assignee-popover { width: 260px; }
        .tl-option-popover { min-width: 160px; }

        .tl-date-popover {
          width: 560px;
          padding: 0;
          overflow: hidden;
        }
        .tl-date-tabs {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          border-bottom: 1px solid #ddd;
          background: #f5f5f5;
        }
        .tl-date-tab {
          background: #fff;
          border: 1px solid #ccc;
          color: #555;
          border-radius: 6px;
          font-size: 12px;
          padding: 6px 14px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .tl-date-tab:hover { border-color: #aaa; }
        .tl-date-popover-close {
          margin-left: auto;
          cursor: pointer;
          color: #888;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 5px;
          border-radius: 4px;
          transition: all 0.2s;
        }
        .tl-date-popover-close:hover {
          color: #1a1a1e;
          background: #e8e8e8;
        }
        .tl-date-tab-active {
          border-color: #4592f0;
          color: #000;
          background: #e0ecff;
        }
        .tl-date-body { display: flex; }
        .tl-date-quick {
          width: 160px;
          border-right: 1px solid #ddd;
          padding: 6px;
        }
        .tl-date-quick-row {
          display: flex;
          justify-content: space-between;
          padding: 7px 10px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          transition: background 0.15s;
        }
        .tl-date-quick-row:hover { background: #f0f0f0; }
        .tl-date-recurring {
          border-top: 1px solid #ddd;
          margin-top: 4px;
          padding-top: 10px;
          color: #666;
        }
        .tl-date-calendar { flex: 1; padding: 10px 16px; }
        .tl-date-cal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;
        }
        .tl-cal-nav {
          cursor: pointer;
          color: #666;
          padding: 2px 8px;
          font-size: 16px;
          border-radius: 4px;
          transition: all 0.2s;
        }
        .tl-cal-nav:hover {
          color: #000;
          background: #f0f0f0;
        }
        .tl-date-cal-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
          text-align: center;
        }
        .tl-date-cal-dow {
          color: #666;
          font-size: 11px;
          margin-bottom: 4px;
          padding: 4px 0;
        }
        .tl-cal-cell {
          padding: 6px 0;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          transition: all 0.15s;
        }
        .tl-cal-cell:hover { background: #f0f0f0; }
        .tl-cal-cell-empty { cursor: default; }
        .tl-cal-cell-empty:hover { background: transparent; }
        .tl-cal-cell-today {
          color: #f1c232;
          font-weight: 700;
          border: 1px solid #f1c232;
        }
        .tl-cal-cell-selected {
          background: #4592f0;
          color: #fff;
          border-radius: 6px;
        }
        .tl-cal-cell-disabled {
          color: #ccc;
          cursor: not-allowed;
          pointer-events: none;
        }
        .tl-cal-cell-disabled:hover { background: transparent; }
        .tl-date-quick-row-disabled {
          color: #ccc;
          cursor: not-allowed;
        }
        .tl-date-quick-row-disabled:hover { background: transparent; }

        .tl-comment-popover {
          width: 380px;
          right: 0;
          left: auto;
          padding: 12px;
        }
        .tl-comment-thread-list {
          max-height: 260px;
          overflow-y: auto;
          margin-bottom: 10px;
        }
        .tl-comment-item {
          display: flex;
          gap: 10px;
          margin-bottom: 12px;
          padding: 4px 0;
        }
        .tl-comment-text {
          color: #1a1a1e;
          word-wrap: break-word;
        }
        .tl-comment-item-actions {
          display: flex;
          gap: 10px;
          margin-top: 4px;
          font-size: 12px;
        }
        .tl-reply {
          color: #666;
          cursor: pointer;
          margin-left: auto;
          transition: color 0.2s;
        }
        .tl-reply:hover { color: #000; }

        .tl-comment-editor {
          border: 1px solid #ccc;
          border-radius: 8px;
          overflow: hidden;
          background: #fff;
        }
        .tl-comment-files {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          padding: 6px 10px 0;
        }
        .tl-comment-file-chip {
          background: #f0f0f0;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 11px;
          padding: 2px 8px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .tl-comment-format-bar {
          display: flex;
          gap: 12px;
          padding: 6px 10px;
          border-bottom: 1px solid #ddd;
          background: #f9f9f9;
          color: #666;
        }
        .tl-comment-format-bar span {
          cursor: pointer;
          padding: 2px 4px;
          border-radius: 4px;
          transition: all 0.2s;
        }
        .tl-comment-format-bar span:hover {
          color: #000;
          background: #e8e8e8;
        }
        .tl-comment-input {
          min-height: 60px;
          padding: 10px;
          font-size: 13px;
          color: #1a1a1e;
          outline: none;
          background: #fff;
        }
        .tl-comment-input:empty::before {
          content: attr(data-placeholder);
          color: #999;
        }
        .tl-comment-toolbar {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 6px 10px;
          border-top: 1px solid #ddd;
          background: #f9f9f9;
        }
        .tl-comment-tool {
          color: #666;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          padding: 4px 6px;
          border-radius: 4px;
          transition: all 0.2s;
        }
        .tl-comment-tool:hover {
          color: #000;
          background: #e8e8e8;
        }
        .tl-comment-tool-label {
          font-size: 12px;
          gap: 4px;
          padding: 4px 10px;
          border: 1px solid #ccc;
          border-radius: 4px;
        }
        .tl-comment-tool-label:hover { border-color: #aaa; }
        .tl-comment-send {
          margin-left: auto;
          background: #4592f0;
          border: none;
          color: #fff;
          border-radius: 6px;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }
        .tl-comment-send:hover {
          background: #5a9ff5;
          transform: scale(1.05);
        }
        .tl-comment-send:disabled {
          background: #ccc;
          color: #888;
          cursor: not-allowed;
          transform: none;
        }
        .tl-emoji-popover {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 4px;
          width: 200px;
          padding: 8px;
        }
        .tl-emoji-item {
          cursor: pointer;
          font-size: 18px;
          text-align: center;
          padding: 4px;
          border-radius: 4px;
          transition: background 0.2s;
        }
        .tl-emoji-item:hover {
          background: #f0f0f0;
        }

        .tl-stage-add-bottom {
          display: flex;
          align-items: center;
          padding: 8px 16px;
          color: #555;
          font-size: 13px;
          cursor: pointer;
          border-top: 1px solid #ddd;
          transition: all 0.2s;
          border-radius: 0 0 8px 8px;
          background: #fafafa;
        }
        .tl-stage-add-bottom:hover {
          color: #000;
          background: #f0f0f0;
        }

        .tl-add-row {
          display: grid;
          grid-template-columns: 1fr auto auto;
          align-items: center;
          padding: 6px 16px;
          background: #fafafa;
          border-top: 1px solid #ddd;
        }
        .tl-add-input {
          background: transparent;
          border: none;
          color: #1a1a1e;
          font-size: 13px;
          width: 100%;
          padding: 6px 0;
        }
        .tl-add-input:focus { outline: none; }
        .tl-add-input::placeholder { color: #888; }
        .tl-add-row-icons {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #555;
        }
        .tl-add-icon-pill {
          display: inline-flex;
          align-items: center;
          border: 1px solid #ccc;
          border-radius: 4px;
          padding: 3px 10px;
          font-size: 12px;
          color: #333;
        }
        .tl-add-icon {
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s;
        }
        .tl-add-icon:hover {
          color: #000;
          background: #e8e8e8;
        }
        .tl-add-row-actions { display: flex; gap: 8px; }
        .tl-btn-cancel, .tl-btn-save {
          font-size: 12px;
          border-radius: 6px;
          padding: 5px 14px;
          border: 1px solid #ccc;
          background: transparent;
          color: #1a1a1e;
          cursor: pointer;
          transition: all 0.2s;
        }
        .tl-btn-cancel:hover {
          background: #f0f0f0;
          border-color: #aaa;
        }
        .tl-btn-save {
          background: #7B3FE4;
          border-color: #7B3FE4;
          color: #fff;
        }
        .tl-btn-save:hover {
          background: #6a35cc;
          transform: scale(1.02);
        }

        .tl-status-modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(20, 20, 24, 0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .tl-status-modal {
          width: 420px;
          max-width: 92vw;
          background: #fff;
          border-radius: 10px;
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }
        .tl-status-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          font-size: 15px;
          font-weight: 600;
          color: #1a1a1e;
          border-bottom: 1px solid #eee;
        }
        .tl-status-modal-close {
          cursor: pointer;
          color: #888;
          display: inline-flex;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s;
        }
        .tl-status-modal-close:hover {
          color: #1a1a1e;
          background: #f0f0f0;
        }
        .tl-status-modal-body {
          padding: 18px 20px 6px;
        }
        .tl-status-modal-message {
          margin: 0 0 12px;
          font-size: 13px;
          color: #1a1a1e;
        }
        .tl-status-modal-label {
          display: block;
          font-size: 12px;
          color: #666;
          margin-bottom: 6px;
        }
        .tl-status-modal-textarea {
          width: 100%;
          min-height: 90px;
          resize: vertical;
          border: 1px solid #ccc;
          border-radius: 6px;
          padding: 10px;
          font-size: 13px;
          color: #1a1a1e;
          font-family: inherit;
        }
        .tl-status-modal-textarea:focus {
          outline: none;
          border-color: #7B3FE4;
        }
        .tl-status-modal-textarea::placeholder { color: #999; }
        .tl-status-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 16px 20px;
        }
        .tl-status-modal-submit {
          font-size: 12px;
          border-radius: 6px;
          padding: 5px 16px;
          border: 1px solid #a13b58;
          background: #a13b58;
          color: #fff;
          cursor: pointer;
          transition: all 0.2s;
        }
        .tl-status-modal-submit:hover {
          background: #8c3249;
        }
        .tl-assignee-more {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #eee;
  color: #555;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
  flex-shrink: 0;
}
        .tl-status-modal-submit:disabled,
        .tl-btn-cancel:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default ProjectTaskListView;